// Updated: Geocoding moved to PharmacyMap component
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as http from "../../../utils/http";
import { sendFilterCannabisEvent } from "../../../utils/sendFilterCannabisEvent";
import { trackContinueToShipping } from "../../../utils/otcPopupExperiment";
import { CatalogSettingContext } from '../ChooseTreatmentRouter';

import { sorted } from "../../../utils/helpers";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ProductSelectionPanel from './components/ProductSelectionPanel';
import { loadCustomerFromSession, useCustomer } from '../../../customHooks/useCustomer';
import HeaderController from '../../../components/Headers/HeaderController';
import Footer from '../../../components/Footer/Footer';
import LoadingModal from '../../../components/Modals/LoadingModal/LoadingModal';
import { ImSpinner2 } from 'react-icons/im';
import ServerError from '../../../components/ServerError/ServerError';
import StickyNotification from '../../../components/StickyNotification/StickyNotification';
import { mergeArrays } from '../helpers';
import ProductSearch from './components/ProductSearch';
import ProductFiltersControl from './components/ProductFiltersControl';
import { FilterMenu } from './components/FiltersMenu';
import { CannabisProductTypeFilter } from '../filters/CannabisProductTypeFilter';
import ShippingAndServiceDropdowns from './components/ShippingAndServiceSelection';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import { setCustomData } from '../../../PixelTrackers/KameleoonTracker';
import { useAppContext } from '../../../providers/AppProvider';
import StickySubmitButton from './components/StickySubmitButton';
import { CANNALEO, DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT, FLOWZZ, FLOWZZ_PREMIUM, ORDER_FLOWS, CANNABIS_LIMIT_ERROR_CODES } from '../../../utils/consts';
import PortaledModal from '../../../components/Modals/PortaledModal';
import Disclaimer from '../components/Disclaimers/Disclaimer';
import CloseIcon from '../../../icons/X.svg';
import ViewOrder from './components/ViewOrder';
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS } from '../../../utils/consts';
import { parseRangeFilter, sortRangeFilter, getMinPricePerQuantity, visibleMinPricePerQuantity, isOrderRateLimited, getCannabisLimitMessage, medicationLinePrice } from '../helpersV2';
import { saveCartToStorage, loadCartFromStorage, clearCartFromStorage, CartTabDTO } from './cartsStorage';
import { useCTProductSearch } from './hooks/useCTProductSearch';
import { PredefinedFiltersType, AdditionalData, ChooseTreatmentProductType, ChooseTreatmentProductItemType, PurchaseTierFilterType, SortByType, CannabisLimitsResult, CTFacets } from '../types';
import ProductDetailsCard from './components/ProductDetailsCard';
import { CANNABIS_SERVICE_FEE_TEST, SERVICE_FEE_TEST_NAME, SERVICE_FEE_TEST_VARIANT, SERVICE_FEE_VARIANTS, ServiceFeeTestVariant } from '../../../utils/consts';
import { LabelColor } from 'data-types/src/LabelTypes';
import { ExpressStockOption, CustomerAddress } from 'data-types';
import { PharmacyMap } from '../../../components/PharmacyPicker';
import * as constants from "./constants";
import { PharmacySearch } from '../../../components/PharmacyPicker';
import { PharmacyCard } from '../../../components/PharmacyPicker';
import { PharmacyList } from '../../../components/PharmacyPicker';
import { UnknownStockConfirmationModal } from './components/UnknownStockConfirmationModal';
import { translate } from '../../../utils/translator';
import { getFavoriteProductCatalogIds } from '../../../utils/favoritesStorage';
import { recordRecentlyViewedProductCatalogId } from '../../../utils/recentlyViewedStorage';
import { ABTestManager } from '../../../utils/ABTestManager';
import SelfPickupWalkthrough from './components/SelfPickupWalkthrough';
import { getStockCodesForTier, isExpressStateFallback } from './components/expressDeliveryUtils';
import { CannabisPharmacyPickerProvider } from './components/CannabisPharmacyPickerProvider';
import AlternativeOfferModalChooseTreatment from '../../../components/Modals/AlternativeOfferModal/AlternativeOfferModalChooseTreatment';

const DEBOUNCE_DELAY = 750;

const computeCartKey = (
  shippingMethod: string,
  selectedService: string,
  expressTier: string | null,
): CartKeyType => {
  if (selectedService === constants.SERVICE_PRESCRIPTION_ONLY) return constants.SERVICE_PRESCRIPTION_ONLY;
  if (shippingMethod === constants.EXPRESS_SHIPPING && expressTier) return `${constants.EXPRESS_SHIPPING}_${expressTier}` as CartKeyType;
  return shippingMethod as CartKeyType;
};
const MAX_SEARCH_EVENT_COUNT = 150;
const MEDIAN_QUANTITY = 20;


export type LabelData = {
    text: string;
    color: LabelColor;
}
export type ProductItem = ChooseTreatmentProductItemType & {
  catalog_treatment_fee: number,
  official_price: number,
  express_stock: boolean,
  catalog_id: number,
  preSelected: boolean,
  self_pickup_stock: boolean,
  selected_stock_code?: string | null
}

export type Product = Omit<ChooseTreatmentProductType, "items"> & {
  catalog_treatment_fee: number,
  preSelected: boolean,
  isSelected: boolean,
  express_stock: boolean,
  available_stocks?: {stockID: number, stockCode: string, pricePerUnit: number}[],
  self_pickup_stock: boolean,
  available_external_pharmacies?: string[], // Array of external pharmacy IDs where product is available (availability 1 or 2)
  items: ProductItem[], // override ProductItemCommonType with local type
  labelData: LabelData | null
  description: string
  image?: {
    base_path: string,
    name: string
  } | null
  manufacturer_name: string
  visiblePrice?: number // The actual visible price shown to user based on shipping method and stock selection
  is_pre_orderable?: boolean
}

type AutomaticPricingRule = {
  range_start: number;
  range_end: number | null;
  quantity: number;
  price_type: string;
  price_value: number;
}

type CategoryCatalog = {
  id: number;
  content_type: string;
}

type ShippingMethodType = typeof constants.SHIPPING | typeof constants.EXPRESS_SHIPPING | typeof constants.PICKUP;
type ServiceType = typeof constants.SERVICE_PRESCRIPTION_ONLY | typeof constants.SERVICE_PRESCRIPTION_AND_MEDICATION;
type CartKeyType = ShippingMethodType | typeof constants.SERVICE_PRESCRIPTION_ONLY | typeof constants.EXPRESS_TIER_KEYS.SAME_DAY | typeof constants.EXPRESS_TIER_KEYS.UBER_2H;
type ApprovalServiceType = typeof constants.SERVICE_FAST_APPROVAL | typeof constants.SERVICE_STANDARD_APPROVAL;

// Constants
const SHIPPING_COMPANIES = {
  UBER: "uber",
  URBIFY: "urbify",
  LIEFERANDO: "lieferando"
}



export interface ChooseTreatmentContextType {
  allowedCannabisGramsQuota: number;
  cannabisLimits: CannabisLimitsResult | null;
  nextDesiredProductItem: ProductItem | undefined;
  desiredProductItem: ProductItem | null;
  setDesiredProductItem: React.Dispatch<React.SetStateAction<ProductItem | null>>;
  language: string;
  maxCartProducts: number;
  products: Product[];
  filteredProducts: Product[];
  filteredProductsTotal: number;
  selectedProducts: Product[];
  selectedProductItems: ProductItem[];
  handleProductToggle: (params: { selectedProduct: Product; desiredProductItem: ProductItem; toggleAction: "ADD" | "REMOVE"; stockCodesInRangeParam?: string[]; shippingMethodParam?: ShippingMethodType }) => void;
  handleProductItemToggle: (productItem: ProductItem, toggleAction: "ADD" | "REMOVE") => void;
  defaultProductItemIndex: (product: Product) => number
  onSubmit: () => void;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  categoryLimitation: null | { monthlyLimitation: number; purchasedQuantity: number };
  submitLoading: boolean;
  setSubmitLoading: React.Dispatch<React.SetStateAction<boolean>>;
  filterData: { [key: string]: { value: string, isSelected: boolean, name: string, keyWordTitle: string, filterFn: (product: Product) => boolean }[] },
  toggleFilter: (category: string, value: string, isMultiSelect?: boolean) => void,
  isFilterMenuOpen: boolean;
  setIsFilterMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  PurchaseTierFilter: PurchaseTierFilterType;
  setPurchaseTierFilter: React.Dispatch<React.SetStateAction<PurchaseTierFilterType>>;
  resetAllFilters: () => void;
  appliedFiltersCount: number;
  hidePrices: boolean;
  productCardRefs: React.RefObject<HTMLDivElement[]>;
  selectedProductForDetails: Product | null;
  setSelectedProductForDetails: React.Dispatch<React.SetStateAction<Product | null>>;
  setViewOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedProductItems: React.Dispatch<React.SetStateAction<ProductItem[]>>;
  calculateTotalMedicationPrice: () => number;
  calculateTotalTreatmentFee: () => number;
  allProductsTotalTreatmentFee: () => number;
  calculateTotalShippingFee: () => number;
  calculateTotalServiceFee: () => number;
  // HL-9738: cannabis service fee A/B test breakdown for the View Order helper text/tooltip.
  getServiceFeeTestInfo: () => { variant: ServiceFeeTestVariant | null; isVariantB: boolean; isVariantC: boolean; flowerCount: number; nonFlowerPresent: boolean };
  calculateFastApprovalFee: () => number;
  fastApprovalFeeFromServer: number;
  setShowMaxLimitModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExceededMaxQuotaModal: React.Dispatch<React.SetStateAction<boolean>>;
  scrollToMainTitle: () => void;
  treatAllAsRegular: boolean;
  setTreatAllAsRegular: React.Dispatch<React.SetStateAction<boolean>>;
  isExpressShippingInRange: boolean;
  isExpressFlow: boolean;
  isExpressUberFlow: boolean;
  isExpressUrbifyFlow: boolean;
  isFastApprovalFlow: boolean;
  sortBy: SortByType,
  setSortBy: React.Dispatch<React.SetStateAction<SortByType>>,
  searchEventCount: React.MutableRefObject<number>;
  selectedStockCode: string | null;
  cart: Cart | null;
  setCart: React.Dispatch<React.SetStateAction<Cart | null>>;
  shippingMethod: ShippingMethodType;
  setShippingMethod: React.Dispatch<React.SetStateAction<ShippingMethodType>>;
  selectedService: ServiceType;
  setSelectedService: React.Dispatch<React.SetStateAction<ServiceType>>;
  selectedApprovalService: ApprovalServiceType;
  setSelectedApprovalService: React.Dispatch<React.SetStateAction<ApprovalServiceType>>;
  handleProductDetailsOpen: (product: Product) => void;
  setExpressShippingCompany: React.Dispatch<React.SetStateAction<string|null>>;
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  parseProducts: (products: Product[], isExpressFlow: boolean, stockCodesInRange: string[]) => Product[];
  customerZipcode: string;
  setCustomerZipcode: React.Dispatch<React.SetStateAction<string>>;
  setSelectedStockCode: React.Dispatch<React.SetStateAction<string | null>>;
  setStockCodesInRange: React.Dispatch<React.SetStateAction<string[]>>;
  stockCodesInRange: string[];
  expressStockOptions: ExpressStockOption[];
  setExpressStockOptions: React.Dispatch<React.SetStateAction<ExpressStockOption[]>>;
  selectedExpressTier: 'uber_2h' | 'same_day' | null;
  setSelectedExpressTier: React.Dispatch<React.SetStateAction<'uber_2h' | 'same_day' | null>>;
  handleExpressTierSelect: (tier: 'uber_2h' | 'same_day') => void;
  handleSameDayCutoffExpired: () => void;
  disableUpQuantity: boolean;
  disableAddProduct: boolean;
  reachedMaxQuota: boolean;
  totalQuantity: number;
  isSelfPickupFlow: boolean;
  isExpressPrescriptionReview: boolean;
  setIsExpressPrescriptionReview: React.Dispatch<React.SetStateAction<boolean>>;
  allPharmaciesLocations: { [key: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; } };
  setAllPharmaciesLocations: React.Dispatch<React.SetStateAction<{ [key: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; } }>>;
  setNearbyPharmacies: React.Dispatch<React.SetStateAction<Array<{ stockCode: string; name: string; address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; distance: number; productsCount?: number }>>>;
  filteredPharmaciesForPickup: { [key: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; external_integration_code?: string; } };
  showSparkleLabel: boolean;
  onExpressLabelClick: () => void;
  onSelfPickupLabelClick: () => void;
  trackSelfPickupView: () => void;
  isExpressNotEligible: boolean;
  // Uber-flow label handlers consumed by the Legacy (non-Urbify) layout's shared children.
  // Optional: the V2/Urbify layout does not provide them. See FreeDosageChooseTreatmentV2Legacy.
  onUberLabelClick?: () => void;
  isUberNotEligible?: boolean;
  resetAllCarts: () => void;
  resetMethodSelection: () => void;
  resetExpressTierCarts: () => void;
  isReturningCustomer: boolean;
  isCannabisReturningCustomer: boolean;
  onFavoriteChange: () => void;
  isUrbifyFlow?: boolean;
  customerAddresses: CustomerAddress[];
  oosPositionsWithOffers: Set<string>;
}

export const ChooseTreatmentContext = createContext<ChooseTreatmentContextType | null>(null);

// How long filters must stay unchanged before the HubSpot filter event is sent.
const FILTER_EVENT_DEBOUNCE_MS = 1000;

const FreeDosageLayoutV2 = () => {

  const { country, language, category } = useParams() as {
    category: string;
    country: string;
    language: string;
  };

  const { customerState } = useCustomer();


  const initFilters = (products: Product[], predefinedFilters?: PredefinedFiltersType, hasFavorites?: boolean, preselectedFilters?: { medicalEffect?: string | null, productType?: string | null }, facets?: CTFacets) => {
    const initializedFilterData = {} as typeof filterData;
    const parsedFiltersFnsMap: Record<string, { filterValue: string, filterFn: (product: Product) => boolean, name: string, keyWordTitle: string }[]> = {}

    if (predefinedFilters) {
      // format for efficient look up + create filtering functions
      const filterTypes = Object.keys(predefinedFilters); // e.g. ["thc", "cbd", "genetic", "type"...]
      filterTypes.forEach((type: string) => {
        const filterType = type.toLowerCase();
        const predefinedFilter = predefinedFilters[filterType];
        const displayTitle = Object.keys(predefinedFilter)[0]; // e.g CBD-Gehalt, THC-Gehalt, Genetik, Effekte ...
        
        const pushFilter = (
          value: string,
          fn: (product: ChooseTreatmentProductType) => boolean,
          name: string
        ) => {
          parsedFiltersFnsMap[filterType].push({
            filterValue: value,
            filterFn: fn,
            name, // the name  will be displayed to the user
            keyWordTitle: displayTitle, // the keyWordTitle will be displayed to the user as well
          });
        };

        const filters = predefinedFilter[displayTitle];

        parsedFiltersFnsMap[filterType] = [];
        switch (filterType) {
          case "thc":
          case "cbd":
            filters.forEach((filter: { value: string, name: string }) => {
              const parsedRangeFilter = parseRangeFilter(filter.value); // a function that checks if a number is in a specific range (encoded by a string such as gte0-lte5)
              const filterFunction = (product: ChooseTreatmentProductType) => {
                const productAttribute = product.additional_data?.[filterType as keyof AdditionalData]; // extract value of position from additional data to filter by
                return parsedRangeFilter(productAttribute); // apply filter on extracted value e.g thc in range [0, 10], or cbd >= 15 ...
              }
              pushFilter(
                filter.value, // eg  gte0-lte5, gte5-lte15, gte15
                filterFunction,
                filter.name // eg Niedrig (<5%), Mittel (5-15%), Hoch (>15%)
              ); 
            });
            break;

          case "genetic":
            filters.forEach((filter: { value: string, name: string }) => {
              const { value: filterValue } = filter;
              const filterFunction = (product: ChooseTreatmentProductType) => {
                const genetic = product.additional_data?.[filterType as keyof AdditionalData];
                if (typeof genetic !== "string") return false;
                return genetic.toLowerCase() === filterValue.toLowerCase();
              }
              
              pushFilter(
                filter.value, // eg  "Sativa", "Indica", "Hybrid"
                filterFunction,
                filter.name // eg  "Sativa", "Indica", "Hybrid"
              ); 
            });

            // add the "all" option to genetic,
            pushFilter(
              "all",
              () => true,
              "Alle"
            );
            break;

          case "effects":
            filters.forEach((filter: { value: string, name: string }) => {
              const filterFn = (product: ChooseTreatmentProductType) => {
                const effects = product.additional_data?.[filterType as keyof AdditionalData];
                if (!Array.isArray(effects) || !effects.every((item) => typeof item === 'string') || effects.length === 0) return false;
                const normalizedValues = effects.map((item) => item.toLowerCase());
                return normalizedValues.includes(filter.value.toLowerCase());
              };              
              
              pushFilter(
                filter.value, // eg  "relaxed, sleepy, focussed", "happy"...
                filterFn,
                filter.name // eg  "Entspannend", "Gesellig", "Aufgeregt", Fokussiert" ...
              ); 
            });
            break;

          case "type":
            filters.forEach((filter: { value: string, name: string }) => {
              const { value: filterValue } = filter;
              const filterFunction = (product: ChooseTreatmentProductType) => {
                const type = product.additional_data?.[filterType as keyof AdditionalData];
                if (typeof type !== "string") return false;
                return type.toLowerCase() === filterValue.toLowerCase();
              }
              
              pushFilter(
                filter.value, // eg  "flower", "extract", "vape", "edible"
                filterFunction,
                filter.name // eg  "Blüten", "Extrakte", "Vape", "Edibles"
              ); 
            });
            break;

          case "medical_effects":
            filters.forEach((filter: { value: string, name: string }) => {
              const filterFn = (product: ChooseTreatmentProductType) => {
                const medicalAttrs = product.additional_data?.medical_attributes;
                if (!Array.isArray(medicalAttrs) || medicalAttrs.length === 0) return false;
                return medicalAttrs.includes(filter.value);
              };

              pushFilter(
                filter.value,
                filterFn,
                filter.name
              );
            });
            break;
        }
      });

      //  price range filter
      const priceRanges = [
        { value: "lte6", name: "< 6€" },
        { value: "gte6-lte10", name: "6€ - 10€" },
        { value: "gte10", name: "> 10€" },
      ];

      initializedFilterData.price = priceRanges.map(({ value, name }) => ({
        value,
        isSelected: false,
        name,
        keyWordTitle: "Preis",
        filterFn: (product: Product) => parseRangeFilter(value)(product.minPricePerQuantity),
      }));

      for (const product of products) {
        if (product.additional_data) {
          for (const [key, value] of Object.entries(product.additional_data)) {
            
            if (value) {
              // skip proprties we do not offer filters for
              if (!["thc", "cbd", "genetic", "effects", "type"].includes(key)) {
                continue;
              }

              // Determine the filterValue based on the key (thc, cbd, genetic, effects, type)
              const filterObj = parsedFiltersFnsMap[key]?.find(({ filterValue, filterFn }) => filterFn(product));
              
              if (filterObj) {
                const filterOptions = [{
                  value: filterObj.filterValue?.toLowerCase() ?? "",
                  isSelected: false,
                  name: filterObj.name ?? "",
                  keyWordTitle: filterObj.keyWordTitle ?? "",
                  filterFn: filterObj.filterFn,
                }];

                // Initialize filterData[key] if not already initialized
                if (!initializedFilterData[key]) {
                  initializedFilterData[key] = [];
                }

                initializedFilterData[key] = mergeArrays(initializedFilterData[key], filterOptions, (a: { value: string }) => a.value);
              }
            }
          }
        }

        const medicalAttrs = product.additional_data?.medical_attributes;
        if (Array.isArray(medicalAttrs) && medicalAttrs.length > 0 && parsedFiltersFnsMap["medical_effects"]) {
          for (const filterObj of parsedFiltersFnsMap["medical_effects"]) {
            if (filterObj.filterFn(product)) {
              if (!initializedFilterData["medical_effects"]) {
                initializedFilterData["medical_effects"] = [];
              }
              const filterOptions = [{
                value: filterObj.filterValue ?? "",
                isSelected: false,
                name: filterObj.name ?? "",
                keyWordTitle: filterObj.keyWordTitle ?? "",
                filterFn: filterObj.filterFn,
              }];
              initializedFilterData["medical_effects"] = mergeArrays(initializedFilterData["medical_effects"], filterOptions, (a: { value: string }) => a.value);
            }
          }
        }
      }


      // The loop above can only reveal options present in the products currently loaded. With
      // server-side paging that is one page, which hid whole filters (on a 2.4k-product catalog:
      // 1 of 3 genetics, 1 of 4 types, 3 of 12 effects). `facets` carries the distinct values for
      // the entire catalog, so add any option the page did not happen to contain. The filterFn
      // still comes from predefinedFilters, so selecting one behaves exactly as before.
      if (facets) {
        const facetKeyToFilterType: Array<[keyof CTFacets, string]> = [
          ["genetic", "genetic"],
          ["type", "type"],
          ["effects", "effects"],
          ["medical_attributes", "medical_effects"],
        ];

        for (const [facetKey, filterType] of facetKeyToFilterType) {
          const facetValues = facets[facetKey];
          if (!facetValues?.length || !parsedFiltersFnsMap[filterType]?.length) continue;

          for (const facetValue of facetValues) {
            // The index stores these with their own casing ("Relaxed"), predefinedFilters uses the
            // configured casing; match case-insensitively rather than trusting either side.
            const filterObj = parsedFiltersFnsMap[filterType].find(
              ({ filterValue }) => filterValue?.toLowerCase() === facetValue.toLowerCase()
            );
            if (!filterObj) continue;

            initializedFilterData[filterType] = mergeArrays(
              initializedFilterData[filterType] ?? [],
              [{
                value: filterObj.filterValue?.toLowerCase() ?? "",
                isSelected: false,
                name: filterObj.name ?? "",
                keyWordTitle: filterObj.keyWordTitle ?? "",
                filterFn: filterObj.filterFn,
              }],
              (a: { value: string }) => a.value
            );
          }
        }
      }

      // sorting thc and cbd e.g "<5" => "5-10" => "">10" ...
      initializedFilterData.cbd &&= sortRangeFilter(initializedFilterData.cbd);
      initializedFilterData.thc &&= sortRangeFilter(initializedFilterData.thc);
    }

    // Favourite filter: only when variable is on and user is in the 50% AB test "Show" variant
    if (hasFavorites) {
      initializedFilterData.favourite = [{
        value: 'show_favourites',
        isSelected: false,
        name: translate(language, 'choose_treatment', 'filters/show_my_favourite'),
        keyWordTitle: translate(language, 'choose_treatment', 'filters/favourite'),
        filterFn: (product: Product) => getFavoriteProductCatalogIds().includes(product.catalog_id),
      }];
    }

    if (preselectedFilters?.medicalEffect && initializedFilterData["medical_effects"]) {
      initializedFilterData["medical_effects"] = initializedFilterData["medical_effects"].map(filterObj => ({
        ...filterObj,
        isSelected: filterObj.value === preselectedFilters.medicalEffect ? true : filterObj.isSelected,
      }));
    }

    if (preselectedFilters?.productType && initializedFilterData["type"]) {
      initializedFilterData["type"] = initializedFilterData["type"].map(filterObj => ({
        ...filterObj,
        isSelected: filterObj.value === preselectedFilters.productType ? true : filterObj.isSelected,
      }));
    }

    setFilterData(initializedFilterData);
  }

  // Set by user filter/sort actions, read by the filter-change effect that sends the HubSpot event.
  const filterChangedByUserRef = useRef(false);

  const toggleFilter = (category: string, value: string, isMultiSelect: boolean = true) => {
    filterChangedByUserRef.current = true;
    // thc, cbd, genetic, effects, price
    setFilterData((prevFilterData) => ({
      ...prevFilterData,
      [category]: prevFilterData[category].map(filterObj => (
        {
          ...filterObj,
          isSelected:
            filterObj.value === value ?
              !filterObj.isSelected :
              isMultiSelect ? filterObj.isSelected : false
        }
      ))
    }));
  }

  //States
  const navigate = useNavigate();
  // Starts true: the mount effect fetches the products, so the first paint is the loader, not an empty page.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectError = searchParams.get("ec");
  const [stockCodesInRange, setStockCodesInRange] = useState<string[]>([]);
  const isExpressShippingInRange = stockCodesInRange && stockCodesInRange.length > 0 && stockCodesInRange[0].length > 0 ? true : false;
  const [products, setProducts] = useState<Product[]>([]);
  // Off-page products the flow refers to (deep link, cart, alternative offer); see the server's
  // anchorCatalogIds. Looked up like list products, never rendered as the list.
  const [anchorProducts, setAnchorProducts] = useState<Product[]>([]);
  const loadedProducts = useMemo(() => [...products, ...anchorProducts], [products, anchorProducts]);
  const selectedProducts = useMemo(() => (sorted(products.filter(product => product.isSelected), (a, b) => a.product_id - b.product_id)), [products])
  const [selectedProductItems, setSelectedProductItems] = useState<ProductItem[]>([]);
  
  // Separate carts for each shipping method, prescription-only service, and express tier.
  // [constants.EXPRESS_SHIPPING] itself is only used transiently before a tier is auto-selected.
  const [cartsByShippingMethod, setCartsByShippingMethod] = useState<Record<CartKeyType, ProductItem[]>>(
    {
      [constants.SHIPPING]: [],
      [constants.EXPRESS_SHIPPING]: [],
      [constants.PICKUP]: [],
      [constants.SERVICE_PRESCRIPTION_ONLY]: [],
      [constants.EXPRESS_TIER_KEYS.UBER_2H]: [],
      [constants.EXPRESS_TIER_KEYS.SAME_DAY]: [],
    } as Record<CartKeyType, ProductItem[]>
  );
  const [categoryLimitation, setCategoryLimitation] = useState<{ monthlyLimitation: number, purchasedQuantity: number } | null>(null);
  const [alternativeOfferState, setAlternativeOfferState] = useState({
    offers: {} as Record<string, any>,
    showModal: false,
    isLoading: false,
    decidedPositions: new Set<string>(),
  });
  const oosPositionsWithOffers = useMemo(() => {
    const positions = new Set(
      Object.entries(alternativeOfferState.offers)
        .filter(([_, offer]: [string, any]) => offer.alternative !== null)
        .map(([position]) => position)
    ); // Result: Set(['p1', 'p3'])  // Only positions with alternatives
    return positions;
  }, [alternativeOfferState.offers]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterData, setFilterData] = useState<{ [key: string]: { value: string, isSelected: boolean, name: string, keyWordTitle: string, filterFn: (product: Product) => boolean }[] }>({});
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [PurchaseTierFilter, setPurchaseTierFilter] = useState<"premium" | "regular">("premium");
  const [searchTerm, setSearchTerm] = useState("");
  const [maxCartProducts, setMaxCartProducts] = useState<number>(3);
  const [cart, setCart] = useState<Cart | null>(null);
  const [expressShippingFee, setExpressShippingFee] = useState<number>(0);
  const [fastApprovalFeeFromServer, setFastApprovalFeeFromServer] = useState<number>(0);
  const [isExpressPrescriptionReview, setIsExpressPrescriptionReview] = useState<boolean>(false);
  const [isReturningCustomer, setIsReturningCustomer] = useState<boolean>(false);
  const [isCannabisReturningCustomer, setIsCannabisReturningCustomer] = useState<boolean>(false);
  const [isFirstSelfPickupOrder, setIsFirstSelfPickupOrder] = useState<boolean>(true);
  const searchEventCount = useRef(0);

  const onFavoriteChange = useCallback(() => {
    const hasFavorites = getFavoriteProductCatalogIds().length > 0;
    setFilterData((prev) => {
      if (hasFavorites && !prev.favourite) {
        return {
          ...prev,
          favourite: [{
            value: 'show_favourites',
            isSelected: false,
            name: translate(language, 'choose_treatment', 'filters/show_my_favourite'),
            keyWordTitle: translate(language, 'choose_treatment', 'filters/favourite'),
            filterFn: (product: Product) => getFavoriteProductCatalogIds().includes(product.catalog_id),
          }],
        };
      }
      if (!hasFavorites && prev.favourite) {
        const { favourite, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [language]);

  const lastHubspotSearchTermRef = useRef("");

  // Forget the last sent term once the search is cleared, so searching it again is sent again.
  useEffect(() => {
    if (!searchTerm.trim()) {
      lastHubspotSearchTermRef.current = "";
    }
  }, [searchTerm]);

  // Search analytics tracking function
  const trackSearchEvent = useCallback((searchTerm: string, results: Product[]) => {
    if (!searchTerm.trim()) {
      return;
    }

    searchEventCount.current += 1;
    
    window.eventsTracking.track({
      e: 'Choose Treatment: Search Performed',
      p: {
      search_term: searchTerm.trim(),
      search_event_count: searchEventCount.current,
      search_results_count: results.length,
      premium_products_count: results.filter(p => p.in_stock).length,
      regular_products_count: results.filter(p => !p.in_stock).length,
      layout: cart?.flow
      }
    });

    // This callback re-runs whenever filters change, and the filter-change effect already
    // sends those. Only send here for a new search term, so a filter change isn't sent twice.
    // sortBy is omitted on purpose: it's declared below this callback.
    if (searchTerm.trim() !== lastHubspotSearchTermRef.current) {
      lastHubspotSearchTermRef.current = searchTerm.trim();
      sendFilterCannabisEvent(
        {
          filterData,
          purchaseTierFilter: PurchaseTierFilter,
          searchTerm,
        },
        country
      );
    }

  }, [cart, filterData, PurchaseTierFilter, country]);

  const [selectedExternalPharmacy, setSelectedExternalPharmacy] = useState<ExternalPharmacy | null>(null);
  const productCardRefs = useRef<HTMLDivElement[]>([]);
  const [showMaxLimitModal, setShowMaxLimitModal] = useState<boolean>(false);
  const [viewOrder, setViewOrder] = useState<boolean>(false);
  const [treatAllAsRegular, setTreatAllAsRegular] = useState<boolean>(false);
  const [expressShippingCompany, setExpressShippingCompany] = useState<string | null>(null);
  const [expressStockOptions, setExpressStockOptions] = useState<ExpressStockOption[]>([]);
  const [selectedExpressTier, setSelectedExpressTier] = useState<'uber_2h' | 'same_day' | null>(null);
  const [isExpressFlow, setIsExpressFlow] = useState<boolean>(false);
  const [isExpressUberFlow, setIsExpressUberFlow] = useState<boolean>(false);
  const [isExpressUrbifyFlow, setIsExpressUrbifyFlow] = useState<boolean>(false);
  const [isFastApprovalFlow, setIsFastApprovalFlow] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortByType>(null);

  // Filters apply on every tap and there are several ways to change them (filters menu,
  // inline product type bar, sort, reset), so send one event once the user stops changing
  // them. The ref keeps setup changes (initFilters, favourites) from counting.
  useEffect(() => {
    if (!filterChangedByUserRef.current) {
      return;
    }

    const timeout = setTimeout(() => {
      filterChangedByUserRef.current = false;
      sendFilterCannabisEvent(
        {
          filterData,
          purchaseTierFilter: PurchaseTierFilter,
          sortBy,
          searchTerm,
        },
        country
      );
    }, FILTER_EVENT_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [filterData, sortBy]);
  const [selectedStockCode, setSelectedStockCode] = useState<string | null>(null);
  const [highlightedStockCode, setHighlightedStockCode] = useState<string | null>(null);
  const [pharmacySearchQuery, setPharmacySearchQuery] = useState<string>("");
  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const { stickyNotification, setStickyNotification, headerVersion } = useAppContext();
  const [showProductDetails, setShowProductDetails] = useState<boolean>(false);
  const [selectedProductForDetails, setSelectedProductForDetails] = useState<Product | null>(null);
  const stockCodeFromParam = searchParams.get("sc") || null; //stock_code
  const stockIDToForceSelfPickup = searchParams.get("sid") || null; //stock_id to force 
  const [isCannaleoFlow, setIsCannaleoFlow] = useState<boolean>(false);
  const [shippingPricingRules, setShippingPricingRules] = useState<AutomaticPricingRule[] | undefined>(undefined);
  const [selfPickupVariable, setSelfPickupVariable] = useState<number>(-1);
  const [urbifyVarible, setUrbifyVariable] = useState<number>(-1);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethodType>(constants.SHIPPING);
  const [selectedService, setSelectedService] = useState<ServiceType>(constants.SERVICE_PRESCRIPTION_AND_MEDICATION);
  const [selectedApprovalService, setSelectedApprovalService] = useState<ApprovalServiceType>(constants.SERVICE_STANDARD_APPROVAL);
  const [allowedCannabisGramsQuota, setAllowedCannabisGramsQuota] = useState<number>(DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT);
  const [cannabisLimits, setCannabisLimits] = useState<CannabisLimitsResult | null>(null);
  const [showUnknownStockModal, setShowUnknownStockModal] = useState<boolean>(false);
  const [pendingPharmacySelection, setPendingPharmacySelection] = useState<string | null>(null);
  
  // Self-Pickup Walkthrough state
  const [walkthroughStep, setWalkthroughStep] = useState<number>(0);
  const [showWalkthrough, setShowWalkthrough] = useState<boolean>(false);

  const hasUserInteracted = useRef<boolean>(false);

  const parseProducts = useCallback((products: Product[], isExpressFlow: boolean, stockCodesInRange: string[]) => {
    return products.map((product) => {
      const isExpressProduct = (isExpressFlow && product.available_stocks?.some(stock => stockCodesInRange?.includes(stock.stockCode))) || false;
      const parsedItems = product.items.map((item) => {
        const isExpressProductItem = (isExpressFlow && item.available_stocks?.some(stock => stockCodesInRange?.includes(stock.stockCode))) || false;
        return { ...item, express_stock: isExpressProductItem };
      });
      return { ...product, items: parsedItems, minPricePerQuantity: getMinPricePerQuantity(product), isSelected: false, express_stock: isExpressProduct, visiblePrice: visibleMinPricePerQuantity(product, shippingMethod, stockCodesInRange, selectedStockCode)};
    });
  }, [shippingMethod, selectedStockCode]);

  // Get shouldShowWalkthrough from server (via context)
  const catalogContext = useContext(CatalogSettingContext) as any;
  const shouldShowWalkthrough = catalogContext?.shouldShowWalkthrough ?? false;
  const urbifyOnboardingEligible = catalogContext?.urbifyOnboardingEligible ?? false;

  // Mark walkthrough as completed in localStorage. The Urbify Same-Day onboarding (HL-9096)
  // uses its own key so its seen-state stays independent of the Uber walkthrough.
  const markAsCompleted = () => {
    if (abTest.trigger === 'urbify') {
      localStorage.setItem('urbify_onboarding_completed', 'true');
    } else {
      localStorage.setItem('new_self_pickup_walkthrough_completed', 'true');
    }
  };

  // Walkthrough display logic - check server probability AND localStorage
  const abTest = useMemo(() => {

    const oldLocalStorageKey = localStorage.getItem('self_pickup_walkthrough_completed');

    if (oldLocalStorageKey) {
      localStorage.removeItem('self_pickup_walkthrough_completed');
    }

    if (localStorage.getItem('abtest_express_address_test')) {
      localStorage.removeItem('abtest_express_address_test');
    }

    // Urbify Same-Day onboarding (HL-9096): deterministic (no probability gate), shown once
    // per device for the Urbify-eligible-but-not-Uber segment. Own seen-key keeps it
    // independent of the Uber walkthrough below.
    const urbifyCompleted = localStorage.getItem('urbify_onboarding_completed');
    if (urbifyOnboardingEligible && isExpressFlow && urbifyCompleted !== 'true') {
      return { variant: 'variant', showWalkthrough: true, showSparkleLabel: true, trigger: 'urbify' as const };
    }

    // Check if already completed in localStorage
    const completed = localStorage.getItem('new_self_pickup_walkthrough_completed');
    if (completed === 'true') {
      return { variant: 'control', showWalkthrough: false, showSparkleLabel: false, trigger: null };
    }
    
    // Show walkthrough only if express flow is active and server says to show (probability check passed)
    if (shouldShowWalkthrough && isExpressFlow) {
      return { variant: 'variant', showWalkthrough: true, showSparkleLabel: true, trigger: 'uber' as const };
    }
    
    // Don't show: either express flow off or server probability says don't show
    return { variant: 'control', showWalkthrough: false, showSparkleLabel: false, trigger: null };
  }, [shouldShowWalkthrough, isExpressFlow, urbifyOnboardingEligible]);

  // Track any user interaction before walkthrough; only show walkthrough if no interaction in first 2s
  useEffect(() => {
    if (!abTest.showWalkthrough || loading) {
      return;
    }

    const markInteracted = () => {
      hasUserInteracted.current = true;
    };

    const events: (keyof DocumentEventMap)[] = ['click', 'keydown', 'touchstart'];
    events.forEach((ev) => document.addEventListener(ev, markInteracted, true));

    const timer = setTimeout(() => {
      events.forEach((ev) => document.removeEventListener(ev, markInteracted, true));
      if (!hasUserInteracted.current) {
        setWalkthroughStep(shippingMethod === constants.PICKUP ? 2 : 1);
        setShowWalkthrough(true);
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
      events.forEach((ev) => document.removeEventListener(ev, markInteracted, true));
    };
  }, [abTest.showWalkthrough, loading]);

  const handleWalkthroughClose = () => {
    setShowWalkthrough(false);
    setWalkthroughStep(0);
    markAsCompleted();
  };

  const handleWalkthroughComplete = () => {
    setShowWalkthrough(false);
    setWalkthroughStep(0);
    markAsCompleted();
  };

  const isSelfPickupFlow = useMemo(() => {
    if (selfPickupVariable === 1) return true;
    if (selfPickupVariable === 0) return false;
    if (stockIDToForceSelfPickup) return true;

    return new ABTestManager({
      testName: 'self_pickup',
      variants: ['Show', 'Hide'],
      weights: [selfPickupVariable, 1 - selfPickupVariable],
    }).getVariant() === 'Show';
  }, [selfPickupVariable, stockIDToForceSelfPickup]);

  const isUrbifyFlow = useMemo(() => {
    // The shipping page later reads this same 'urbify' ABTestManager's localStorage
    // entry (abtest_urbify) to decide whether to display the Urbify tier there, since
    // it has no other way to know the rollout decision made on this page. Every branch
    // below must persist its result via the manager, not just the weighted fallback,
    // or that flag is left unset and Urbify silently stops showing on the shipping page.
    const urbifyABTest = new ABTestManager({
      testName: 'urbify',
      variants: ['Show', 'Hide'],
      weights: urbifyVarible >= 0 ? [urbifyVarible, 1 - urbifyVarible] : undefined,
    });

    if (urbifyVarible === 1 || abTest.trigger === 'urbify') {
      urbifyABTest.setVariant('Show');
      return true;
    }
    if (urbifyVarible === 0) {
      urbifyABTest.setVariant('Hide');
      return false;
    }

    return urbifyABTest.getVariant() === 'Show';
  }, [urbifyVarible]);


  const trackSelfPickupView = useCallback(() => {
    MainTracker.track('custom', 'Funnel Self Pickup View', {
      user_id: customerState?.customerID || null,
      first_time_order: isReturningCustomer ? 'No' : 'Yes',
      first_time_self_pickup_order: isFirstSelfPickupOrder ? 'Yes' : 'No',
    });
  }, [customerState, isReturningCustomer, isFirstSelfPickupOrder]);

  const onExpressLabelClick = useCallback(() => {
    setWalkthroughStep(1);
    setShowWalkthrough(true);
  }, []);

  const onSelfPickupLabelClick = useCallback(() => {
    resetAllCarts();
    setShippingMethod(constants.PICKUP);
    setSelectedService(isSelfPickupFlow ? constants.SERVICE_PRESCRIPTION_AND_MEDICATION : constants.SERVICE_PRESCRIPTION_ONLY);
    setPurchaseTierFilter(isSelfPickupFlow ? 'premium' : 'regular');
    setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
    setIsExpressPrescriptionReview(false);
    trackSelfPickupView();
    // Defer opening walkthrough step 2 until after Self-Pickup tab content has rendered
    requestAnimationFrame(() => {
      setTimeout(() => {
        setWalkthroughStep(2);
        setShowWalkthrough(true);
      }, 0);
    });
  }, [isSelfPickupFlow, trackSelfPickupView]);

  const isExpressBlockFallback = expressStockOptions.length > 0 && isExpressStateFallback(expressStockOptions);
  const isExpressNotEligible = shippingMethod === constants.EXPRESS_SHIPPING && (!isExpressShippingInRange || expressStockOptions.length === 0 || isExpressBlockFallback);

  const getCartKey = useCallback(
    () => computeCartKey(shippingMethod, selectedService, selectedExpressTier),
    [selectedService, shippingMethod, selectedExpressTier],
  );

  // Track previous cart key to detect changes
  const prevCartKeyRef = useRef<CartKeyType | null>(null);
  const currentCartKey = getCartKey();

  // Sync selectedProductItems with cartsByShippingMethod when items change
  useEffect(() => {
    if (prevCartKeyRef.current === currentCartKey) {
      setCartsByShippingMethod(prev => ({
        ...prev,
        [currentCartKey]: selectedProductItems,
      }));
    }
  }, [selectedProductItems, currentCartKey]);

  // Load cart when cart key changes (shipping method or service type change)
  useEffect(() => {
    if (prevCartKeyRef.current === null) {
      prevCartKeyRef.current = currentCartKey;
      return;
    }

    if (prevCartKeyRef.current !== currentCartKey) {
      // Bare EXPRESS_SHIPPING is transient; tier selection claims current cart.
      const isInitialExpressTierResolution =
        prevCartKeyRef.current === constants.EXPRESS_SHIPPING &&
        currentCartKey !== constants.EXPRESS_SHIPPING &&
        currentCartKey.startsWith(`${constants.EXPRESS_SHIPPING}_`);

      if (isInitialExpressTierResolution) {
        setCartsByShippingMethod(prev => ({
          ...prev,
          [constants.EXPRESS_SHIPPING]: [],
          [currentCartKey]: selectedProductItems,
        }));
        prevCartKeyRef.current = currentCartKey;
        return;
      }

      const savedCart = cartsByShippingMethod[currentCartKey];

      setCartsByShippingMethod(prev => ({
        ...prev,
        [prevCartKeyRef.current!]: selectedProductItems,
      }));

      // load products to exists cart
      if (savedCart && savedCart.length > 0) {
        setSelectedProductItems(savedCart);

        setProducts(current => {
          const savedProductIds = new Set(savedCart.map(item => {
            const product = current.find(p => p.items.some(i => i.product_item_id === item.product_item_id));
            return product?.product_id;
          }).filter(Boolean));
          return current.map(product => ({
            ...product,
            isSelected: savedProductIds.has(product.product_id)
          }));
        });
      } else {
        setSelectedProductItems([]);
        setProducts(current => current.map(product => ({
          ...product,
          isSelected: false
        })));
      }

      prevCartKeyRef.current = currentCartKey;
    }
  }, [currentCartKey]);

  // Persist the full cart (all tabs) to localStorage after every change so it survives a refresh.
  useEffect(() => {
    if (loading || error !== null) return;
    const cartId = window.eventsTracking?.getData?.("cart_id");
    if (!cartId) return;
    const orderFlow = selectedService === constants.SERVICE_PRESCRIPTION_ONLY
      ? constants.SERVICE_PRESCRIPTION_ONLY
      : shippingMethod;
    const cartsByTab: Partial<Record<string, CartTabDTO[]>> = {};
    for (const [tabKey, items] of Object.entries(cartsByShippingMethod) as [string, ProductItem[]][]) {
      if (items.length > 0) {
        cartsByTab[tabKey] = items.map(item => ({
          product_item_id: item.product_item_id,
          catalog_id: item.catalog_id,
          selected_stock_code: item.selected_stock_code ?? null,
          quantity: item.quantity,
        }));
      }
    }
    saveCartToStorage(cartId, { orderFlow, selectedStockCode, selectedExpressTier, cartsByTab });
  }, [loading, error, cartsByShippingMethod, shippingMethod, selectedService, selectedStockCode, selectedExpressTier]);

  const [treatmentPricingRules, setTreatmentPricingRules] = useState<AutomaticPricingRule[] | undefined>(undefined);
  const [categoryCatalog, setCategoryCatalog] = useState<CategoryCatalog>({content_type: "", id: 0});
  const isSwissCannabis = country?.toUpperCase() === "CH" && categoryCatalog.content_type === "cannabis";

  const [cannabisNetworkPrescriptionPrice, setCannabisNetworkPrescriptionPrice] = useState<number>(0);
  const [customerZipcode, setCustomerZipcode] = useState<string>("");
  const [allPharmaciesLocations, setAllPharmaciesLocations] = useState<{ [key: string]: { name: string, address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; } }>({});
  const [desiredProductItem, setDesiredProductItem] = useState<ProductItem | null>(null);
  const [nearbyPharmacies, setNearbyPharmacies] = useState<Array<{ stockCode: string; name: string; address: string; location?: { lat: number; lng: number }; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; distance: number; productsCount?: number }>>([]);
  
  const [showExceededMaxQuotaModal, setShowExceededMaxQuotaModal] = useState<boolean>(false);
  const totalQuantity = useMemo(() => selectedProductItems.reduce((acc, item) => acc + item.quantity, 0), [selectedProductItems]);

    // Catalog-wide counts from the search index; `products` only holds the first page.
    const [serverPharmacyProductCounts, setServerPharmacyProductCounts] = useState<{ [stockCode: string]: number } | null>(null);

    // Calculate products count per pharmacy
    const calculateProductsCountPerPharmacy = useMemo(() => {
      if (serverPharmacyProductCounts) {
        return serverPharmacyProductCounts;
      }

      const counts: { [stockCode: string]: number } = {};

      products.forEach((product) => {
        // For internal pharmacies
        if (product.available_stocks) {
          product.available_stocks.forEach((stock) => {
            counts[stock.stockCode] = (counts[stock.stockCode] || 0) + 1;
          });
        }
        
        // For external pharmacies
        if (product.available_external_pharmacies) {
          product.available_external_pharmacies.forEach((stockCode) => {
            counts[stockCode] = (counts[stockCode] || 0) + 1;
          });
        }
      });
      
      return counts;
    }, [products, serverPharmacyProductCounts]);
  
  // Filter pharmacies based on service type when on SELF PICKUP:
  // - Prescription only: show only external pharmacies
  // - Premium service (prescription + medication): show only internal pharmacies
  const filteredPharmaciesForPickup = useMemo(() => {
    if (shippingMethod === constants.PICKUP && allPharmaciesLocations) {
      if (selectedService === constants.SERVICE_PRESCRIPTION_ONLY) {
        // Only external pharmacies for prescription only.
        // Show pharmacies with known stock (integration code + products) OR pharmacies without
        // an integration code (no product catalog, but still valid pickup locations).
        return Object.fromEntries(
          Object.entries(allPharmaciesLocations).filter(
            ([stockCode, pharmacy]) =>
              pharmacy.is_external_pharmacy === true &&
              (calculateProductsCountPerPharmacy[stockCode] > 0 || !pharmacy.external_integration_code)
          )
        );
      } else if (selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION) {
        // Only internal pharmacies for premium service
        return Object.fromEntries(
          Object.entries(allPharmaciesLocations).filter(
            ([stockCode, pharmacy]) =>
              pharmacy.is_external_pharmacy === false && calculateProductsCountPerPharmacy[stockCode] > 0
          )
        );
      }
    }

    return allPharmaciesLocations || {};
  }, [shippingMethod, selectedService, allPharmaciesLocations, calculateProductsCountPerPharmacy]);

  const externalPharmacyIds = useMemo(() => {
    return Object.entries(allPharmaciesLocations)
      .filter(([, pharmacy]) => pharmacy.is_external_pharmacy === true)
      .map(([stockCode]) => stockCode);
  }, [allPharmaciesLocations]);

  const availablePharmacyStockCodes = useMemo(() => {
    return Object.keys(filteredPharmaciesForPickup);
  }, [filteredPharmaciesForPickup]);

  // A prescription-only pharmacy without an integration code has no stock data, so the whole catalog
  // is shown instead of scoping the search to it. Such a pharmacy is never in the index's
  // external_pharmacy_ids, so scoping would return nothing.
  const isUnknownStockPharmacySelected = useMemo(() => {
    if (selectedService !== constants.SERVICE_PRESCRIPTION_ONLY || !selectedStockCode) return false;
    const pharmacy = allPharmaciesLocations[selectedStockCode];
    return pharmacy?.is_external_pharmacy === true && !pharmacy.external_integration_code;
  }, [selectedService, selectedStockCode, allPharmaciesLocations]);

  const searchSelectedStockCode = isUnknownStockPharmacySelected ? null : selectedStockCode;
  const searchAvailablePharmacyStockCodes = useMemo(
    () => (isUnknownStockPharmacySelected ? [] : availablePharmacyStockCodes),
    [isUnknownStockPharmacySelected, availablePharmacyStockCodes]
  );

  const [manufacturerId, setManufacturerId] = useState<number | null>(null);

  const ctSearch = useCTProductSearch({
    categoryCatalogId: categoryCatalog.id,
    countryId: country,
    languageId: language,
    manufacturerId,
    parseProducts,
    isExpressFlow,
    stockCodesInRange,
    isSwiss: country?.toUpperCase() === "CH",
    isCannabis: categoryCatalog.content_type === "cannabis",
    shippingMethod,
    purchaseTier: PurchaseTierFilter,
    selectedStockCode: searchSelectedStockCode,
    externalPharmacyIds,
    availablePharmacyStockCodes: searchAvailablePharmacyStockCodes,
  });

  // Every product this page has loaded, keyed by item, for the item→product lookups below. The
  // DB-direct page built this once from the full catalog; here products keep arriving (paging,
  // search, anchors), so it follows them.
  const itemToProductMap = useMemo(() => {
    const map = new Map<number, Product>();
    [...ctSearch.serverProducts, ...anchorProducts, ...products].forEach((product: Product) => {
      product.items.forEach((item) => map.set(item.product_item_id, product));
    });
    return map;
  }, [ctSearch.serverProducts, anchorProducts, products]);

  const desiredProductItemIndex = selectedProductForDetails?.items.findIndex((item) => item.product_item_id === desiredProductItem?.product_item_id) || 0;
  const nextDesiredProductItem = selectedProductForDetails?.items[desiredProductItemIndex + 1] || selectedProductForDetails?.items[desiredProductItemIndex];


  const nextQuantity = useMemo(() => nextDesiredProductItem?.quantity || 0, [nextDesiredProductItem]);
  const currentQuantity = useMemo(() => desiredProductItem?.quantity || 0, [desiredProductItem]);
  const rateLimited = useMemo(() => isOrderRateLimited(cannabisLimits), [cannabisLimits]);
  const cannabisLimitMessage = useMemo(() => getCannabisLimitMessage(cannabisLimits, language), [cannabisLimits, language]);

  const disableUpQuantity = useMemo(() => rateLimited || (totalQuantity + nextQuantity) > allowedCannabisGramsQuota, [totalQuantity,currentQuantity, nextQuantity, allowedCannabisGramsQuota, rateLimited]);
  const disableAddProduct = useMemo(() => rateLimited || (totalQuantity + currentQuantity) > allowedCannabisGramsQuota, [totalQuantity, nextQuantity, currentQuantity, allowedCannabisGramsQuota, rateLimited]);
  const reachedMaxQuota = useMemo(() => rateLimited || totalQuantity >= allowedCannabisGramsQuota, [totalQuantity, allowedCannabisGramsQuota, rateLimited]);

  const handleExpressTierSelect = (tier: 'uber_2h' | 'same_day') => {
    resetAllCarts();
    setSelectedExpressTier(tier);
    setExpressShippingCompany(tier === 'uber_2h' ? 'uber' : 'urbify');

    const tierScopedCodes = getStockCodesForTier(expressStockOptions, tier);
    setStockCodesInRange(tierScopedCodes);

    const allTiers = expressStockOptions.flatMap(opt => opt.tiers);
    const selectedTierData = allTiers.find(t => t.tier === tier && t.isAvailable);
    if (selectedTierData) {
      setExpressShippingFee(selectedTierData.fee);
    }

    const reparsed = parseProducts(products, true, tierScopedCodes);
    setProducts(reparsed);
  };

  // Live cutoff cross-over: the countdown reaching 00:00 means same-day is no longer
  // orderable. Flip isCutoffPassed on every same_day tier so resolveExpressBlockState
  // recomputes the block into the 'sameDayDisabled' state (reason: same_day_disabled_cutoff),
  // exactly as if the server had reported the cutoff passed on the next lookup.
  const handleSameDayCutoffExpired = () => {
    setExpressStockOptions(current =>
      current.map(opt => ({
        ...opt,
        tiers: opt.tiers.map(t =>
          t.tier === 'same_day' ? { ...t, isCutoffPassed: true } : t
        ),
      }))
    );

    // If same-day was the active selection, the cart was built on now-dead same-day stock.
    // Clear the whole express selection so the customer re-picks from the updated options.
    // Uber / no selection are unaffected — leave them alone.
    if (selectedExpressTier === 'same_day') {
      resetAllCarts();
    }
  };

  const resetAllCarts = () => {
    setExpressShippingCompany(null);
    setSelectedExpressTier(null);
    setSelectedStockCode(null);
    // Preserve prescription-only UX: keep "regular" filter when on only-prescription; do not switch to premium
    setPurchaseTierFilter(selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? 'regular' : 'premium');
    setSelectedProductItems([]);
    setNearbyPharmacies([]);
    setProducts(current => {
        return current.map(product =>  ({...product, isSelected: false}))
    })
    setCartsByShippingMethod(prev => ({
      ...prev,
      [constants.SHIPPING]: [],
      [constants.EXPRESS_SHIPPING]: [],
      [constants.PICKUP]: [],
      [constants.SERVICE_PRESCRIPTION_ONLY]: [],
      [constants.EXPRESS_TIER_KEYS.UBER_2H]: [],
      [constants.EXPRESS_TIER_KEYS.SAME_DAY]: [],
    }));
  }

  // Reset method-specific state (tier, company, pharmacy) but preserve cart for save/restore effect.
  const resetMethodSelection = () => {
    setExpressShippingCompany(null);
    setSelectedExpressTier(null);
    setSelectedStockCode(null);
    setNearbyPharmacies([]);
  }

  // Wipe only Express tier carts on toggle-off; preserve other method carts.
  const resetExpressTierCarts = () => {
    setCartsByShippingMethod(prev => ({
      ...prev,
      [constants.EXPRESS_TIER_KEYS.UBER_2H]: [],
      [constants.EXPRESS_TIER_KEYS.SAME_DAY]: [],
    }));
  }
  
  const API_PATH = useMemo(() => {
    if (stockCodeFromParam) return "choose-treatment-by-stock";
    return "choose-treatment-free-dosage-v2";
  }, [stockCodeFromParam]);

  const getMaxAvailableAmountItem = (items: ProductItem[]) => {
    
    // Start from the highest quantity and work down to find the first available item
    if (shippingMethod === constants.EXPRESS_SHIPPING) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].express_stock) {
          return items[i];
        }
      }
    } else if (shippingMethod === constants.SHIPPING) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].in_stock) {
          return items[i];
        }
      }
    } else if (shippingMethod === constants.PICKUP) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].self_pickup_stock) {
          return items[i];
        }
      }
    }
    return items[0];
  }


  const defaultProductItemIndex = (product: Product) => {

    const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;

    if (isProductInStock && !treatAllAsRegular) {
      let filteredProductItems = product.items.filter((item: any) => item.in_stock || item.express_stock || item.self_pickup_stock);

      if (allowedCannabisGramsQuota > 0) {
        const filtered = filteredProductItems.filter((item) => item.quantity <= (allowedCannabisGramsQuota - totalQuantity));
        filteredProductItems = filtered.length > 0 ? filtered : filteredProductItems;
      }

      // If no items are available, return 0
      if (filteredProductItems.length === 0) {
        return 0;
      }
      
      const maxAmountItem = getMaxAvailableAmountItem(filteredProductItems);
      // Safety check for maxAmountItem
      if (!maxAmountItem) {
        return 0;
      }
      
      let medianItemIndex = filteredProductItems.findIndex((item) => item.product_item_id === maxAmountItem.product_item_id);
      // Guard: if medianItemIndex is -1, return 0
      if (medianItemIndex === -1) {
        return 0;
      }
      let medianProductItem = filteredProductItems[medianItemIndex];

      // Safety check for medianProductItem
      if (!medianProductItem) {
        return 0;
      }
      
      if (medianProductItem.quantity > MEDIAN_QUANTITY) {
        medianItemIndex = filteredProductItems.findIndex((item) => item.quantity === MEDIAN_QUANTITY);
        if (medianItemIndex == -1) {
          for (const productItem of filteredProductItems) {
            if (productItem.quantity > MEDIAN_QUANTITY) {
              medianItemIndex = filteredProductItems.findIndex((item) => item.quantity === productItem.quantity);
            }
          }
        }
      }

      if (product.quantity_type !== 'gram') {
        medianItemIndex = product.items.findIndex((item) => item.in_stock);
        if (medianItemIndex === -1) {
          medianItemIndex = 0;
        }
      }
      return medianItemIndex;
    }

    return 0;
  }

  

  // Memos 
  // Determine if the selected products section should be shown
  const shouldShowSelectedProductsSection = useMemo(() => {
    return selectedProducts.length > 0;
  }, [selectedProducts.length]);


  const appliedFiltersCount = useMemo(() => {
    let count = 0;

    if (!filterData) {
      return 0;
    }

    // # 1 cannabis filters + price + express-delivery
    const filterObjects = Object.values(filterData).flat();
    const activeFilters = filterObjects.filter((item) => item.isSelected);
    count += activeFilters.length;


    // # 2 sorting
    count+= sortBy ? 1 : 0;

    return count;
  }, [filterData, PurchaseTierFilter, sortBy]);


  // Purchase tier side effects (treatAllAsRegular flag)
  useEffect(() => {
    if (PurchaseTierFilter === 'premium') {
      setTreatAllAsRegular(false);
    } else if (PurchaseTierFilter === 'regular') {
      setTreatAllAsRegular(true);
    } else if (PurchaseTierFilter === 'all' && selectedProducts.length === 0) {
      setTreatAllAsRegular(false);
    }
  }, [PurchaseTierFilter, selectedProducts.length]);

  const filteredProducts = useMemo(() => {
    let filtered: Product[] = ctSearch.serverInitialized ? ctSearch.serverProducts : products;

    // Remove products that are already selected (in cart)
    const selectedProductIds = new Set(selectedProducts.map(product => product.product_id));
    if (shouldShowSelectedProductsSection) {
      filtered = filtered.filter(product => !selectedProductIds.has(product.product_id));
    }

    // Calculate visible price for each product
    filtered = filtered.map(product => {
      const desiredItem = selectedProductItems.find(item => item.catalog_id === product.catalog_id);
      return {
        ...product,
        visiblePrice: visibleMinPricePerQuantity(
          product,
          shippingMethod,
          stockCodesInRange,
          selectedStockCode,
          desiredItem
        )
      };
    });

    return filtered;

  }, [ctSearch.serverProducts, ctSearch.serverInitialized, products, selectedProductItems, selectedProducts, shouldShowSelectedProductsSection, shippingMethod, selectedStockCode, stockCodesInRange])
  
  // Track search events when searchTerm changes and we have results
  useEffect(() => {
    if (searchTerm && searchTerm.trim().length > 2 && filteredProducts && searchEventCount.current <= MAX_SEARCH_EVENT_COUNT) {
      // Add a small delay to avoid tracking rapid keystrokes
      const timeoutId = setTimeout(() => {
        trackSearchEvent(searchTerm, filteredProducts);
      }, DEBOUNCE_DELAY); // 500ms delay to debounce rapid typing

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, filteredProducts, trackSearchEvent]);


  
  const setDefaultProductsAndItems = (products: Product[], stockCodesInRange: string[], cartProducts: CartProduct[] = [], orderFlow: { selectedService: ServiceType, shippingMethod: ShippingMethodType }) => {
    // 1. Pre-select all Products:
    const preSelectedProducts = products.filter((product) => product.preSelected);


    const totalPreSelectedQuantity = preSelectedProducts.reduce((acc, cur ) => cur.items.find(item => item.preSelected)?.quantity || 0 + acc, 0);
    
    if (totalPreSelectedQuantity > allowedCannabisGramsQuota) {
      // The rendered list is the search hook's, so the promotion happens there.
      ctSearch.promoteToTop(preSelectedProducts);
      setShowExceededMaxQuotaModal(true);
      return;
    }

    for (const product of preSelectedProducts) {
      let selectedProductItem = product.items.find(item => item.preSelected);
      if (!selectedProductItem) {
        const filteredProductItems = product.items.filter((item: any) => item.in_stock);
        const medianItemIndex = Math.floor(filteredProductItems.length / 4);
        selectedProductItem = filteredProductItems[medianItemIndex];
      }

      handleProductToggle({ selectedProduct: product, desiredProductItem: selectedProductItem, toggleAction: "ADD", stockCodesInRangeParam: stockCodesInRange });
    }

    // If no pre-selected products and cart is empty, open ProductDetailsCard for product matching catalog_id from URL
    // Only for Shipping flow with prescription + medication service
    const isShippingFlow = orderFlow.shippingMethod === constants.SHIPPING;
    const isPrescriptionAndMedication = orderFlow.selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION;  
      
    if (isShippingFlow && isPrescriptionAndMedication && preSelectedProducts.length === 0 && cartProducts.length === 0) {
      const catalogIdFromUrl = searchParams.get("catalog_id");

      if (catalogIdFromUrl) {
        const matchingProduct = products.find(product => product.catalog_id === Number(catalogIdFromUrl));
        const isMatchingProductInStock = matchingProduct?.in_stock;

        if (matchingProduct && isMatchingProductInStock) {
          handleProductDetailsOpen(matchingProduct);
        }
      }
    }
  }



  const handlePharmacySelection = (stockCode: string) => {
    const pharmacy = allPharmaciesLocations[stockCode];
    
    // Check if pharmacy has unknown stock (external pharmacy without integration code)
    const hasUnknownStock = pharmacy?.is_external_pharmacy && !pharmacy?.external_integration_code;
    
    if (hasUnknownStock && selectedService === constants.SERVICE_PRESCRIPTION_ONLY) {
      // Show confirmation modal for unknown stock
      setPendingPharmacySelection(stockCode);
      setShowUnknownStockModal(true);
    } else {
      // Directly set the pharmacy
      setSelectedStockCode(stockCode);
      setHighlightedStockCode(null);
    }
  };

  const confirmUnknownStockSelection = () => {
    if (pendingPharmacySelection) {
      setSelectedStockCode(pendingPharmacySelection);
      setHighlightedStockCode(null);
    }
    setShowUnknownStockModal(false);
    setPendingPharmacySelection(null);
  };

  const cancelUnknownStockSelection = () => {
    setShowUnknownStockModal(false);
    setPendingPharmacySelection(null);
  };

  const checkForAutoSelection = (
    cartProducts: CartProduct[],
    products: Product[],
    eligibleStockCodes: string[],
    orderFlow: { selectedService: ServiceType, shippingMethod: ShippingMethodType }
  ) => {

    const { selectedService, shippingMethod } = orderFlow;
    const selectedCatalogIds = new Set(cartProducts.map(cartProduct => cartProduct.catalog_id));
    const selectedProductItemIDS = new Set(cartProducts.map(cartProduct => cartProduct.product_item_id));


    const isPrescriptionOnly = selectedService === constants.SERVICE_PRESCRIPTION_ONLY;
    for (const product of sorted(products, (a, b) => a.product_id - b.product_id)) {
      if (selectedCatalogIds.has(product.catalog_id)) {
        const matchingProductItem = product.items.find(item => 
          selectedProductItemIDS.has(item.product_item_id)
        );

        if (matchingProductItem) {
          const productSelectedStockCode = cartProducts.find(cartProduct => cartProduct.product_item_id === matchingProductItem.product_item_id)?.selected_stock_code;

          // if product is not available at the selected stock, don't add it to the cart
          if (!isPrescriptionOnly && productSelectedStockCode && matchingProductItem.available_stocks?.every(stock => stock.stockCode !== productSelectedStockCode)) {
            continue;
          }

          // if express shipping and no stock code in range, don't add it to the cart
          if (shippingMethod === constants.EXPRESS_SHIPPING && !matchingProductItem.available_stocks?.some(stock => eligibleStockCodes?.includes(stock.stockCode))) {
            continue;
          }
          
          const productItemToAdd = {...matchingProductItem, selected_stock_code: productSelectedStockCode};
          handleProductToggle({ selectedProduct: product, desiredProductItem: productItemToAdd, toggleAction: "ADD", stockCodesInRangeParam: eligibleStockCodes, shippingMethodParam: shippingMethod });
          setSelectedStockCode(productItemToAdd?.selected_stock_code || null);
          
          // prescription only flow, add the product item to the prescription only cart
          if (isPrescriptionOnly) {
            setCartsByShippingMethod(prev => ({
              ...prev,
              [constants.SERVICE_PRESCRIPTION_ONLY]: [...prev[constants.SERVICE_PRESCRIPTION_ONLY], productItemToAdd],
            }));
            setPurchaseTierFilter('regular');
          }
          
          // prescription + medication flow, add the product item to the regular cart
          else {
            setCartsByShippingMethod(prev => ({
              ...prev,
              [shippingMethod]: [...prev[shippingMethod], productItemToAdd],
            }));
          }
        }
      }
    }
  }

  useEffect(() => {

    const handlePopState = (e: any) => {
      e.preventDefault();
      navigate(
        {pathname: `/questionnaire/${country}/${language}/${category}`, search: searchParams.toString()},
        { replace: true }
      );
    }

    window.addEventListener("popstate", handlePopState);

    setLoading(true);
    setStickyNotification({ show: false, title: "", content: "", level: "", showInfoIcon: false, autoFade: false })
    
    
     http.getFromServer(`${API_PATH}/${category}/${country}/${language}?${searchParams.toString()}`,
      [
        http.MIDDLEWARES.CUSTOMER_ID,
        http.MIDDLEWARES.CART_ORDER
      ])
      .then((response: any) => {
        const serverData = response.data;
        const eligibleStockCodes = serverData.eligibleExpressStocks.length > 0 ? serverData.eligibleExpressStocks : stockCodesInRange;
        setShippingPricingRules(serverData.shippingPricingRules);
        setTreatmentPricingRules(serverData.treatmentPricingRules);
        setCategoryCatalog(serverData.categoryCatalog);
        setCannabisNetworkPrescriptionPrice(serverData.cannabisNetworkPrescriptionPrice);
        const products = parseProducts(serverData.products, serverData.isExpressFlow, eligibleStockCodes);
        const anchorProducts = parseProducts(serverData.anchorProducts ?? [], serverData.isExpressFlow, eligibleStockCodes);
        const loadedProducts = [...products, ...anchorProducts];
        setProducts(products);
        setAnchorProducts(anchorProducts);
        ctSearch.resetServerProducts(
          products,
          serverData.totalProducts ?? products.length,
          serverData.initialSearchScope ?? {},
          [...serverData.products, ...(serverData.anchorProducts ?? [])].filter((product: Product) => product.preSelected),
        );
        setManufacturerId(serverData.manufacturerID ?? null);
        setCustomerZipcode(serverData.cart.zipcode ?? "");
        setIsCannaleoFlow(serverData.cart.flow === ORDER_FLOWS.CANNALEO_PRE_SELECTED || serverData.cart.flow === ORDER_FLOWS.FLOWZZ_PRE_SELECTED);
        setCategoryLimitation(serverData.categoryLimits);
        setSelectedExternalPharmacy(serverData.externalPharmacy);
        setIsReturningCustomer(serverData.isReturningCustomer === true);
        setIsCannabisReturningCustomer(serverData.isCannabisReturningCustomer === true);
        setIsFirstSelfPickupOrder(serverData.isFirstSelfPickupOrder !== false);
        const hasFavorites = getFavoriteProductCatalogIds().length > 0;
        const productsCopy = JSON.parse(JSON.stringify(products));
        const preselectedFilters = {
          medicalEffect: searchParams.get("medical_effect"),
          productType: searchParams.get("product_type") || searchParams.get("filter"),
        };
        initFilters(productsCopy, serverData.predefinedFilters, hasFavorites, preselectedFilters, serverData.facets);
        setMaxCartProducts(serverData.maxCartProducts);
        setIsExpressFlow(serverData.isExpressFlow);
        setIsExpressUberFlow(serverData.isExpressUberFlow ?? false);
        setIsExpressUrbifyFlow(serverData.isExpressUrbifyFlow ?? false);
        setIsFastApprovalFlow(serverData.isFastApprovalFlow);
        setStockCodesInRange(eligibleStockCodes);
        setCart(serverData.cart);
        setAllowedCannabisGramsQuota(serverData.allowedCannabisGramsQuota);
        if (serverData.cannabisLimits) {
          setCannabisLimits(serverData.cannabisLimits);
        }
        const orderFlow = initOrderFlow(serverData.orderFlow);
        const altParam = searchParams.get("alt");
        const cartId = window.eventsTracking?.getData?.("cart_id");
        // Use localStorage when no deep link / redirect override / forced pharmacy are active.
        const localCart = (!altParam && redirectError !== "dnis" && !serverData.stockCodeToForceSelfPickup && cartId)
          ? loadCartFromStorage(cartId)
          : null;

        if (localCart) {
          const restoredFlow = initOrderFlow(localCart.orderFlow);
          if (localCart.selectedStockCode) setSelectedStockCode(localCart.selectedStockCode);
          if (localCart.selectedExpressTier) setSelectedExpressTier(localCart.selectedExpressTier as 'uber_2h' | 'same_day');

          const activeTabKey = computeCartKey(restoredFlow.shippingMethod, restoredFlow.selectedService, localCart.selectedExpressTier);

          // Restore inactive tabs directly into cartsByShippingMethod using fresh product data.
          const restoredInactiveTabs: Partial<Record<string, ProductItem[]>> = {};
          for (const [tabKey, dtos] of Object.entries(localCart.cartsByTab)) {
            if (!dtos?.length || tabKey === activeTabKey) continue;
            const isPrescriptionOnly = tabKey === constants.SERVICE_PRESCRIPTION_ONLY;
            const isExpress = tabKey.startsWith(`${constants.EXPRESS_SHIPPING}_`);
            restoredInactiveTabs[tabKey] = dtos.flatMap((dto: CartTabDTO) => {
              const product = loadedProducts.find((p: Product) => p.catalog_id === dto.catalog_id);
              if (!product) return [];
              const item = product.items.find((i: ProductItem) => i.product_item_id === dto.product_item_id);
              if (!item) return [];
              if (!isPrescriptionOnly && dto.selected_stock_code &&
                  item.available_stocks?.every((s) => s.stockCode !== dto.selected_stock_code)) return [];
              if (isExpress && !item.available_stocks?.some((s) => eligibleStockCodes.includes(s.stockCode))) return [];
              return [{ ...item, selected_stock_code: dto.selected_stock_code }];
            });
          }
          setCartsByShippingMethod(prev => ({ ...prev, ...restoredInactiveTabs }));

          // Restore active tab via checkForAutoSelection to get fresh prices and handle side effects.
          const activeDTOs = localCart.cartsByTab[activeTabKey] ?? [];
          if (activeDTOs.length > 0) {
            checkForAutoSelection(activeDTOs as unknown as CartProduct[], loadedProducts, eligibleStockCodes, restoredFlow);
          }
          setDefaultProductsAndItems(loadedProducts, eligibleStockCodes, [], restoredFlow);
        } else {
          const cartProductsForDefault = altParam ? [] : (serverData.cartProducts || []);
          setDefaultProductsAndItems(loadedProducts, eligibleStockCodes, cartProductsForDefault, orderFlow);
          if (serverData.cartProducts?.length > 0 && redirectError !== "dnis") {
            checkForAutoSelection(serverData.cartProducts, loadedProducts, eligibleStockCodes, orderFlow);
          }
        }
        setAllPharmaciesLocations(serverData.allPharmaciesAddresses);
        setServerPharmacyProductCounts(serverData.pharmacyProductCounts ?? null);
        setSelfPickupVariable(serverData.selfPickupVariable);
        setUrbifyVariable(serverData.urbifyVariable);
        setCustomerAddresses(serverData.customerAddresses || []);
        localStorage.removeItem('abtest_default_selected_service'); 
        if (serverData.expressShippingFee) {
          setExpressShippingFee(serverData.expressShippingFee);
        }
        if (typeof serverData.fastApprovalFee === 'number') {
          setFastApprovalFeeFromServer(serverData.fastApprovalFee);
        }

        if (serverData.stockCodeToForceSelfPickup) {
          setSelectedStockCode(serverData.stockCodeToForceSelfPickup);
          setHighlightedStockCode(null);
          if (serverData.isExternalPharmacyPreselected) {
            setSelectedService(constants.SERVICE_PRESCRIPTION_ONLY);
            setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
            setIsExpressPrescriptionReview(false);
            setPurchaseTierFilter('regular');
          } else {
            setSelectedApprovalService(constants.SERVICE_FAST_APPROVAL);
            setIsExpressPrescriptionReview(true);
          }
        }

        // Restore previous decisions to avoid reopening modal
        let decidedPositionsFromServer = new Set<string>();
        if (serverData.cart?.decided_alternative_positions) {
          try {
            const parsed = JSON.parse(serverData.cart.decided_alternative_positions);
            if (Array.isArray(parsed)) {
              decidedPositionsFromServer = new Set(parsed);
              setAlternativeOfferState(prev => ({ ...prev, decidedPositions: decidedPositionsFromServer }));
            }
          } catch (e) {
            window.clientLogger.error("Failed to parse decided_alternative_positions", e);
          }
        }

        // Read alternative offers for out-of-stock positions from the already-fetched serverData
        const oosParam = searchParams.get("oos");
        if ((oosParam || altParam) && serverData.cart.came_from === FLOWZZ_PREMIUM) {
          // Get A/B test variant from fresh server data
          const alternativeOfferVariable = serverData.alternativeOfferVariable;
          const isAlternativeOfferTreatment = new ABTestManager({
            testName: 'offer_alternative_cannabis_choose_treatment',
            variants: ['Treatment', 'Control'],
            weights: alternativeOfferVariable >= 0 ? [alternativeOfferVariable, 1 - alternativeOfferVariable] : undefined,
          }).getVariant() === 'Treatment';


          MainTracker.track('custom', 'Alternative Offer AB Test Assigned', {
            test_variant: isAlternativeOfferTreatment ? 'Treatment' : 'Control',
          });
          if (isAlternativeOfferTreatment) {
            const fetchedAlternatives: Record<string, any> = serverData.alternatives || {};
            const undecidedAlternatives = Object.fromEntries(
              Object.entries(fetchedAlternatives).filter(([position]) =>
                altParam ? true : !decidedPositionsFromServer.has(position)
              )
            );

            setAlternativeOfferState(prev => ({
              ...prev,
              offers: undecidedAlternatives,
              showModal: Object.keys(undecidedAlternatives).length > 0,
              isLoading: false,
            }));
          }
        }

      })
      .catch((error) => {
        setError(500);
        window.clientLogger.error("error get FreeDosageLayoutV2", error)
      })
      .finally(() => {
        setLoading(false);
      })

      return () => {
        window.removeEventListener("popstate", handlePopState);
      }
  }, [])

  // Auto-open pre-order alternative modal when alternatives load
  useEffect(() => {
    if (oosPositionsWithOffers.size > 0) {
      // Track alternative offer modal shown event
      const totalOosCount = Object.keys(alternativeOfferState.offers).length;

      // Count total positions sent (p1, p2, p3, etc.)
      const params = new URLSearchParams(window.location.search);
      let totalPositionCount = 0;
      for (let i = 1; i <= 3; i++) {
        if (params.has(`p${i}`)) {
          totalPositionCount++;
        }
      }

      MainTracker.track('custom', 'Alternative offer shown', {
        item_stock: totalOosCount,
        item_total: totalPositionCount,
        test_variant: 'Treatment'
      });

      setAlternativeOfferState(prev => ({ ...prev, showModal: true }));
    }
  }, [oosPositionsWithOffers.size]);

  // User left the popup open for 1 minute without deciding 
  const handleAlternativeWait = (position: string) => {
    const positionNum = parseInt(position.replace('p', ''));
    const totalCount = Object.keys(alternativeOfferState.offers).length;
    MainTracker.track('custom', 'Decision on alternative product', {
      decision: 3, // Wait
      item_status: positionNum,
      item_count: totalCount,
    });
  };

  const handleAlternativeAccept = (position: string) => {
    // HL-10739: Track decision = 1 (Accept)
    const positionNum = parseInt(position.replace('p', ''));
    const totalCount = Object.keys(alternativeOfferState.offers).length;
    MainTracker.track('custom', 'Decision on alternative product', {
      decision: 1, // Accepted
      item_status: positionNum, // position of the item in the list (1-based index)
      item_count: totalCount, // total number of items in the list
    });

    setAlternativeOfferState(prev => ({ ...prev, decidedPositions: new Set(prev.decidedPositions).add(position) }));

    const offer = alternativeOfferState.offers[position];
    if (!offer || !offer.alternative || !offer.originalProduct) {
      return;
    }


    // First, remove the original out-of-stock product
    const originalProduct = loadedProducts.find(p => p.product_id === offer.originalProduct.product_id);
    const itemToRemove = originalProduct?.items.find(item => item.quantity === offer.originalProduct.quantity) || originalProduct?.items[0];

    if (originalProduct && itemToRemove) {
      handleProductToggle({ selectedProduct: originalProduct, desiredProductItem: itemToRemove, toggleAction: "REMOVE" });
    }

    // Find the alternative product in the loaded products to get the actual ProductItem
    const alternativeProduct = loadedProducts.find(p => p.catalog_id === offer.alternative.catalogID);
    if (!alternativeProduct) {
      return;
    }

    // Use the item with the same quantity as the original
    const desiredItem = alternativeProduct.items.find(item => item.quantity === offer.originalProduct.quantity) || alternativeProduct.items[0];

    if (!desiredItem) {
      return;
    }

    // Wait for removal to complete before adding (state updates are async)
    // Use handleProductItemToggle directly since handleProductToggle has cart size limit check
    // We're replacing the removed item, so we need to bypass the max cart check
    setTimeout(() => {
      // Also update products array to mark as selected (adding it when it came as an anchor)
      setProducts(current => {
        const existsInProducts = current.some(p => p.product_id === alternativeProduct.product_id);
        const base = existsInProducts ? current : [...current, alternativeProduct];
        return base.map((product) => {
          if (maxCartProducts === 1) {
            return product.product_id === alternativeProduct.product_id
              ? { ...product, isSelected: true }
              : { ...product, isSelected: false };
          } else {
            return product.product_id === alternativeProduct.product_id
              ? { ...product, isSelected: true }
              : product;
          }
        });
      });
      // Add to selected items
      handleProductItemToggle(desiredItem, "ADD");
    }, 50);
  };

  // Close modal and cancel whatever positions the customer never got to (X button / outside click mid-queue). 
  const handleCloseAlternativeModal = () => {
    Object.keys(alternativeOfferState.offers)
      .filter(position => !alternativeOfferState.decidedPositions.has(position))
      .forEach(position => {
        handleAlternativeCancel(position);
      });
    setAlternativeOfferState(prev => ({ ...prev, showModal: false }));
  };

  // Handle alternative cancellation - remove original product item
  const handleAlternativeCancel = (position: string) => {
    // Track decision = 2 (Rejected)
    const positionNum = parseInt(position.replace('p', ''));
    const totalCount = Object.keys(alternativeOfferState.offers).length;
    MainTracker.track('custom', 'Decision on alternative product', {
      decision: 2,
      item_status: positionNum,
      item_count: totalCount,
    });

    // Cancel is a permanent decision too - see the comment in handleAlternativeAccept.
    setAlternativeOfferState(prev => ({ ...prev, decidedPositions: new Set(prev.decidedPositions).add(position) }));

    const offer = alternativeOfferState.offers[position];
    if (!offer || !offer.originalProduct) {
      return;
    }

    // Find the original product in the loaded products
    const originalProduct = loadedProducts.find(p => p.product_id === offer.originalProduct.product_id);
    if (!originalProduct) {
      return;
    }

    // Find the item with matching quantity
    const itemToRemove = originalProduct.items.find(item => item.quantity === offer.originalProduct.quantity);
    if (!itemToRemove && originalProduct.items.length > 0) {
      handleProductToggle({ selectedProduct: originalProduct, desiredProductItem: originalProduct.items[0], toggleAction: "REMOVE" });
      return;
    }

    if (itemToRemove) {
      handleProductToggle({ selectedProduct: originalProduct, desiredProductItem: itemToRemove, toggleAction: "REMOVE" });
    }
  };

  const initOrderFlow = (orderFlow: string) => {
    let newSelectedService: ServiceType;
    let newShippingMethod: ShippingMethodType;

    switch (orderFlow) {
      case constants.SHIPPING:
        newSelectedService = constants.SERVICE_PRESCRIPTION_AND_MEDICATION;
        newShippingMethod = constants.SHIPPING;
        break;
      case constants.EXPRESS_SHIPPING:
        newSelectedService = constants.SERVICE_PRESCRIPTION_AND_MEDICATION;
        newShippingMethod = constants.EXPRESS_SHIPPING;
        break;
      case constants.PICKUP:
        newSelectedService = constants.SERVICE_PRESCRIPTION_AND_MEDICATION;
        newShippingMethod = constants.PICKUP;
        break;
      case constants.SERVICE_PRESCRIPTION_ONLY:
        newSelectedService = constants.SERVICE_PRESCRIPTION_ONLY;
        newShippingMethod = constants.PICKUP;
        break;
      default:
        newSelectedService = constants.SERVICE_PRESCRIPTION_AND_MEDICATION;
        newShippingMethod = constants.SHIPPING;
        break;
    }

    setSelectedService(newSelectedService);
    setShippingMethod(newShippingMethod);

    return { selectedService: newSelectedService, shippingMethod: newShippingMethod };
  }

  useEffect(() => {
    if (searchParams.get("limit_disclaimer") && categoryLimitation) {
      const remainingPossibleQuantity = categoryLimitation?.monthlyLimitation - categoryLimitation?.purchasedQuantity;
      const content = `Ihr persönliches Limit ist derzeit: ${remainingPossibleQuantity > 0 ? remainingPossibleQuantity : 0} Gramm. Das generelle Limit beträgt ${categoryLimitation?.monthlyLimitation} Gramm pro Monat.`
      setStickyNotification({ show: true, title: "Monatliches Limit überschritten", content, level: "disclaimer", autoFade: false, showInfoIcon: false });
    }


    if (searchParams.get("mcpqle") === "true") {
      setShowExceededMaxQuotaModal(true);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("mcpqle");
      setSearchParams(newSearchParams);
    }


  }, [searchParams, categoryLimitation])

  useEffect(() => {
    if (viewOrder || showProductDetails){
      document.body.style.overflow = "hidden";
    }
    else {
      document.body.style.overflow = "auto";
    }
    return () => {
        document.body.style.overflow = "auto"
    };
  }, [viewOrder, showProductDetails]);

  const onSubmit = async () => {

    setSubmitLoading(true);


    // 1. Mix panel events
    selectedProducts.forEach((product, index) => {
      MainTracker.superProperties({ [`Product Name ${index + 1}`]: product.title });
    })

    selectedProductItems.forEach((productItem, index) => {
      MainTracker.superProperties({ [`ProductItemID ${index + 1}`]: productItem.product_item_id });
    })

    let flow = null;
    if (cart?.external_pharmacy_id && searchParams.get("af")) {
      if (searchParams.get("af") == CANNALEO){
        flow = ORDER_FLOWS.CANNALEO_PRE_SELECTED
      }
      else if (searchParams.get("af") == FLOWZZ){
        flow = ORDER_FLOWS.FLOWZZ_PRE_SELECTED
      }
    }
    else if (searchParams.get("sc")) {
        flow = ORDER_FLOWS.STOCK + "_" + searchParams.get("sc")
    }

    // 2. Send to server
    http.postToServer("choose-treatment-free-dosage", {
      flow,
      shippingMethod,
      selectedService,
      cartProductsDTOs: selectedProductItems.slice(0, maxCartProducts).map((item) => {
        return {
          quantity: 1,
          product_in_stock: ((item.in_stock || item.express_stock) && !treatAllAsRegular) ?? 0,
          only_prescription: treatAllAsRegular || !(item.in_stock || item.express_stock || item.self_pickup_stock),
          express_stock: item.express_stock,
          catalog_id: item.catalog_id,
          product_item_id: item.product_item_id,
          is_pre_selected: item.preSelected,
          selected_stock_code: item.selected_stock_code
        }
      }),
      expressShippingCompany,
      expressShippingTier: selectedExpressTier,
      selectedStockCode,
      isExpressPrescriptionReview,
      decidedAlternativePositions: Array.from(alternativeOfferState.decidedPositions)
    }, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
      .then((response) => {
        const cartId = window.eventsTracking?.getData?.("cart_id");
        if (cartId) clearCartFromStorage(cartId);
        let customerFromSession = loadCustomerFromSession();    

        const updatedSearchParams = new URLSearchParams(searchParams); 
        
        const deeplinkCoupon = window.eventsTracking.getData("cpn")
        if (deeplinkCoupon) {
          updatedSearchParams.set("cpn", deeplinkCoupon);
        }
        
        if (!customerFromSession) {
          navigate({ pathname: `/sign_up/${country}/${language}`, search: updatedSearchParams.toString() });
        } else if (cart?.should_id_verify && !isSwissCannabis) {
          navigate({ pathname: `/customer-verification/${country}/${language}`, search: updatedSearchParams.toString() });
        } else if (cart?.consultation_type === "video") {
          navigate({ pathname: `/choose-doctor/${country}/${language}/${category}`, search: updatedSearchParams.toString() });
        } else {
          trackContinueToShipping();
          navigate({ pathname: `/shipping/${country}/${language}`, search: updatedSearchParams.toString() });
        }
      })
      .catch((err) => {
        const code = err?.response?.data?.code;

        if (CANNABIS_LIMIT_ERROR_CODES.includes(code)) {
          const limitContent = cannabisLimitMessage || translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_limit');
          setStickyNotification({ show: true, title: "", content: limitContent, level: "disclaimer", showInfoIcon: false, autoFade: false });
        } 
        else if (code === "selected_stock_code_not_found") {
          const content = "Bitte wählen Sie eine Apotheke für Selbstabholung aus.";
          setStickyNotification({ show: true, title: "Apotheke nicht für Selbstabholung ausgewählt", content, level: "error", showInfoIcon: false, autoFade: true });
          setViewOrder(false);
        } 
        else {
          setStickyNotification({ show: true, title: "Server Failed", content: "", level: "error", showInfoIcon: false, autoFade: false });
        }
      })
      .finally(() => setSubmitLoading(false));
  }



  const updateExpressStockStates = () => {
    if (expressShippingCompany) {
      const hasRemainingExpressProducts = selectedProductItems.some(item => item.express_stock);
      
      if (!hasRemainingExpressProducts) {
        setExpressShippingCompany(null);
      }
    }

    if (selectedStockCode && shippingMethod === constants.EXPRESS_SHIPPING) {
      const hasRemainingProductsWithSameStock = selectedProducts
        .some(product => product.available_stocks?.some(stock => stock.stockCode === selectedStockCode));
      
      if (!hasRemainingProductsWithSameStock) {
        setSelectedStockCode(null);
      }
    }
  }


  const handleProductItemToggle = (selectedProductItem: ProductItem, toggleAction: "ADD" | "REMOVE") => {
    if (toggleAction === "ADD") {

      setSelectedProductItems((current) => {

        // 1. If user may choose only 1 then return only selected product item
        if (maxCartProducts === 1) {
          return [selectedProductItem]
        }

        // 2. Else, get all the selected product items that are not from the same product
        let selectedWithoutSiblings = current.filter((currentItem) => currentItem.catalog_id !== selectedProductItem.catalog_id);

        // 3. Add the selectedProductItem
        return sorted([...selectedWithoutSiblings, selectedProductItem], (a, b) => (itemToProductMap.get(a.product_item_id)?.product_id ?? 0) - (itemToProductMap.get(b.product_item_id)?.product_id ?? 0));
      });


    } else {
      //Remove that product item from selected
      setSelectedProductItems((current) => sorted(current.filter((item) => item.product_item_id !== selectedProductItem.product_item_id), (a, b) => (itemToProductMap.get(a.product_item_id)?.product_id ?? 0) - (itemToProductMap.get(b.product_item_id)?.product_id ?? 0)));
    }
  }


  const handleProductToggle = useCallback(({ selectedProduct, desiredProductItem, toggleAction, stockCodesInRangeParam = stockCodesInRange, shippingMethodParam = shippingMethod }: { selectedProduct: Product; desiredProductItem: ProductItem; toggleAction: "ADD" | "REMOVE"; stockCodesInRangeParam?: string[]; shippingMethodParam?: ShippingMethodType }) => {
    // First, verify if we can add more products to the cart 
    if (selectedProducts.length >= maxCartProducts && toggleAction === "ADD") {
      return
    }

    if (toggleAction === "ADD") {

      setProducts((current) => {
        const existsInProducts = current.some(p => p.product_id === selectedProduct.product_id);
        const base = existsInProducts ? current : [...current, selectedProduct];
        return base.map((product) => {
          if (maxCartProducts === 1) {
            return product.product_id === selectedProduct.product_id
              ? { ...product, isSelected: true }
              : { ...product, isSelected: false };
          } else {
            return product.product_id === selectedProduct.product_id
              ? { ...product, isSelected: true }
              : product;
          }
        });
      });

      const selectedProductPlace = filteredProducts.findIndex((product) => product.product_id === selectedProduct.product_id) + 1;
      const placeOneProduct = filteredProducts[0];
      MainTracker.track("custom", "product_added", {
        chosen_product_id: selectedProduct.product_id,
        chosen_product_name: selectedProduct.title,
        chosen_product_place: selectedProductPlace || null,
        chosen_product_price: selectedProduct.visiblePrice ?? null,
        place_1_product_id: placeOneProduct?.product_id ?? null,
        place_1_product_name: placeOneProduct?.title ?? null,
        place_1_product_price: placeOneProduct?.visiblePrice ?? null,
      });

      const ProductItemToAdd = {
        ...desiredProductItem,
        product_title: selectedProduct.title,
        preSelected: selectedProduct.preSelected // productItem.preselected determined by the product.preSelected
      }
      if (shippingMethodParam === constants.EXPRESS_SHIPPING) {
        if (!selectedExpressTier) {
          const matchedOption = expressStockOptions.find(opt =>
            desiredProductItem.available_stocks?.some(s => s.stockCode === opt.stockCode)
          );
          setExpressShippingCompany(matchedOption?.companyCode || SHIPPING_COMPANIES.UBER);
        }
        if (!selectedStockCode) {
          // Find the nearest (first) in-range stock code that matches available_stocks of the desiredProductItem
          let nearestInRangeStock = "";
          for (const stockCode of stockCodesInRange) {
            const stock = desiredProductItem.available_stocks?.find(s => s.stockCode === stockCode);
            if (stock) {
              nearestInRangeStock = stock.stockCode;
              break;
            }
          }
          setSelectedStockCode(nearestInRangeStock);

          // scroll to the product
          setTimeout(() => {
            scrollToSelectedProduct(selectedProduct);
          }, 100);
        }
      }
      handleProductItemToggle(ProductItemToAdd, toggleAction);


    } else { // Toggle action = REMOVE

      if (selectedProducts.length - 1 === 0) {
        setTreatAllAsRegular(false);
        setSelectedStockCode(null);
      }

      setProducts(current => {
        return current.map(product =>  (product.product_id === selectedProduct.product_id ? {...product, isSelected: false} : product))
      })

      updateExpressStockStates();

      setSelectedProductItems((current) => sorted(current.filter((selectedProductItem) => selectedProductItem.catalog_id !== selectedProduct.catalog_id), (a, b) => (itemToProductMap.get(a.product_item_id)?.product_id ?? 0) - (itemToProductMap.get(b.product_item_id)?.product_id ?? 0)));

    }

  }, [selectedProducts, maxCartProducts, selectedProductItems, submitLoading, filteredProducts, shippingMethod, selectedStockCode, stockCodesInRange, expressStockOptions, selectedExpressTier])


  const scrollToMainTitle = useCallback(() => {
    const headerSearch = document.querySelector("#main-title");
    headerSearch?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    
    const productsPanelTitle = document.querySelector("#left-panels");
    
    if (productsPanelTitle) {
      productsPanelTitle.scrollTo({
            behavior: "smooth",
            top: 0,
        });
    }
  },[selectedProducts]);

  const scrollToSelectedProduct = useCallback((product: Product) => {
    const productCard = productCardRefs.current.find(el => Number(el?.dataset.productId) === Number(product.product_id));
    productCard?.scrollIntoView({
      behavior: "smooth", // Smooth scrolling
      block: "center",    // Align to the top of the viewport
    });


   
  },[selectedProducts]);

  useEffect(() => {
    MainTracker.superProperties({
      "Purchase Type": selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? "Regular" : "Premium",
    });
    setCustomData("purchase_type", selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? "prescription_only" : "premium");
  }, [selectedService]);

  // Kameleoon targeting data (HL-12604): tab on load and on change, selection on every add/remove.
  useEffect(() => {
    setCustomData("delivery_mode", shippingMethod === constants.PICKUP ? "pickup" : shippingMethod === constants.EXPRESS_SHIPPING ? "express" : "standard");
  }, [shippingMethod]);

  useEffect(() => {
    const cartValue = selectedProductItems.reduce((sum, item) => sum + medicationLinePrice(item, shippingMethod, selectedStockCode), 0);
    setCustomData("cart_value_eur", Math.round(cartValue * 100) / 100);
    setCustomData("cart_items_count", selectedProductItems.length);
    setCustomData("has_otc", false); // Choose Treatment lists prescription products only
  }, [selectedProductItems, shippingMethod, selectedStockCode]);



  // HL-9738: distinct-flower breakdown of the in-progress selection (used by the dynamic
  // service fee and the View Order helper text). Flower = parent product quantity_type 'gram'.
  const getServiceFeeTestInfo = () => {
    const stored = localStorage.getItem(`abtest_${SERVICE_FEE_TEST_NAME}`);
    const variant = (SERVICE_FEE_VARIANTS as readonly string[]).includes(stored ?? '') ? stored as ServiceFeeTestVariant : null;
    const isVariantB = variant === SERVICE_FEE_TEST_VARIANT.TEST && country?.toUpperCase() === "DE";
    const isVariantC = variant === SERVICE_FEE_TEST_VARIANT.C && country?.toUpperCase() === "DE";
    const isFlower = (item: ProductItem) =>
      itemToProductMap.get(item.product_item_id)?.quantity_type === "gram";
    const flowerCount = selectedProductItems.filter(isFlower).length;
    const nonFlowerPresent = selectedProductItems.some((item) => !isFlower(item));
    return { variant, isVariantB, isVariantC, flowerCount, nonFlowerPresent };
  };

  const calculateTotalServiceFee = () => {

    if ((cart?.flow && EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(cart?.flow))) {
      if (Number(cannabisNetworkPrescriptionPrice)) {
        return Number(0);
      }
    }

    if (treatAllAsRegular) {
      return 0;
    }

    // HL-9738 variant B/C (DE cannabis): dynamic fee by distinct flower count / non-flower presence.
    const { isVariantB, isVariantC, flowerCount, nonFlowerPresent } = getServiceFeeTestInfo();
    if (isVariantB) {
      if (!selectedProductItems.length) return 0;
      const { FLOWER_FEE_BY_COUNT, NON_FLOWER_FEE, MAX_FLOWERS } = CANNABIS_SERVICE_FEE_TEST;
      if (nonFlowerPresent) return NON_FLOWER_FEE;
      return FLOWER_FEE_BY_COUNT[Math.min(flowerCount, MAX_FLOWERS)] ?? NON_FLOWER_FEE;
    }
    if (isVariantC && !nonFlowerPresent) {
      if (!selectedProductItems.length) return 0;
      const { FLOWER_FEE_BY_COUNT_C, MAX_FLOWERS } = CANNABIS_SERVICE_FEE_TEST;
      return FLOWER_FEE_BY_COUNT_C[Math.min(flowerCount, MAX_FLOWERS)] ?? 0;
    }

    const serviceFee = selectedProductItems.reduce((max: number, product) => {
      return Math.max(max, product.service_fee);
    }, 0);

    return serviceFee;
  }

  const calculateTotalMedicationPrice = () => {

    let result = 0;
    const treatmentFee = calculateTotalTreatmentFee() || 0;
    const consultationFee = cart?.consultation_fee || 0;

    if (treatAllAsRegular) {
      if (cart?.consultation_type === "video") {
        return consultationFee;
      } else {
        return treatmentFee;
      }
    }

    selectedProductItems.forEach((productItem) => {
      if (shippingMethod === constants.SHIPPING && !selectedProductItems[0].in_stock) {
        return;
      }
      result += medicationLinePrice(productItem, shippingMethod, selectedStockCode);
    })


    return result;

  }

  // Calculate fast approval fee - separate function for display in order summary (fee value from server)
  const calculateFastApprovalFee = () => {
    if (selectedApprovalService === constants.SERVICE_FAST_APPROVAL) {
      return fastApprovalFeeFromServer;
    }
    return 0;
  }

  const calculateTotalTreatmentFee = () => {
    // Returns base treatment fee (fast approval fee shown separately in order summary but included in invoice)
    if (selectedProductItems.length === 0 || cart?.consultation_type === "video") {
      return 0;
    }
    
    if (country.toUpperCase() !== "DE") {
      return selectedProductItems.reduce((max: number, product) => Math.max(max, product.catalog_treatment_fee), 0);
    }

    if ((cart?.flow && EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(cart?.flow)) || cart?.came_from === FLOWZZ_PREMIUM) {
      if (Number(cannabisNetworkPrescriptionPrice)) {
        return Number(cannabisNetworkPrescriptionPrice);
      }
    }
  
    let treatmentFee = 0;
  
    if (categoryCatalog.content_type === "cannabis") { 
  
      const totalGrams = selectedProductItems.reduce((sum, product) => {
        return sum + product.quantity
      }, 0);
  
      //find a rule that matches the wanted amount of grams
      let pricingRule = treatmentPricingRules?.find(rule => rule.quantity === totalGrams);
  
      //if no such rule exists, take the max quantity rule
      if (!pricingRule) {
        pricingRule = treatmentPricingRules?.reduce((max, rule) => rule.quantity > max.quantity ? rule : max, treatmentPricingRules[0]);
      }
  
      if (pricingRule) {
        treatmentFee = pricingRule.price_value;
        return treatmentFee;
      }
    }
  
    treatmentFee = selectedProductItems.reduce((max: number, product) => {
      return Math.max(max, product.treatment_fee);
    }, 0);
  
    return treatmentFee;
  }
  const allProductsTotalTreatmentFee = () => {
    return selectedProductItems.reduce((acc, product) => acc + product.treatment_fee, 0) || 0;
  }


  const calculateTotalShippingFee = () => {

    let shippingFee = 0;

    if (treatAllAsRegular) {
      return 0;
    }

    if (selectedProductItems.length === 0) {
      return 0;
    }

    if (!selectedProductItems?.[0]?.in_stock && !selectedProductItems?.[0]?.express_stock) {
      return 0;
    }

    if (shippingMethod === constants.PICKUP) {
      return 0;
    }

    if (shippingMethod === constants.EXPRESS_SHIPPING) {
      return expressShippingFee;
    }

    const totalGrams = selectedProductItems.reduce((acc, productItem) => acc + productItem.quantity, 0);

    //find a rule that matches the wanted amount of grams
    let pricingRule = shippingPricingRules?.find(rule => rule.quantity === totalGrams);

    //if no such rule exists, take the max quantity rule
    if (!pricingRule) {
      pricingRule = shippingPricingRules?.reduce((max, rule) => rule.quantity > max.quantity ? rule : max, shippingPricingRules[0]);
    }

    if (pricingRule) {
      shippingFee = pricingRule.price_value;
      
      return shippingFee ?? 0;
    }


  return selectedProductItems?.reduce((max, product) => Math.max(max, product.shipping_fee), 0) ?? 0;

  }

  const handleProductDetailsOpen = (product: Product) => {
    hasUserInteracted.current = true;
    recordRecentlyViewedProductCatalogId(product.catalog_id);
    setSelectedProductForDetails(product);
    setShowProductDetails(true);
  };

  const handleProductDetailsClose = () => {
    setShowProductDetails(false);
    setSelectedProductForDetails(null);
  };

  const handleFilterMenuToggle = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(isFilterMenuOpen) : value;
    if (newValue) {
      hasUserInteracted.current = true;
    }
    document.body.style.overflow = newValue ? "hidden" : "";
    setIsFilterMenuOpen(newValue);
  };

  const clearPrescriptionOnlySelections = () => {
    if (selectedService === constants.SERVICE_PRESCRIPTION_ONLY) {
      setProducts(products => products.map(product => ({
        ...product, 
        isSelected: false, 
        selected_stock_code: null,
        items: product.items.map(item => ({...item, selected_stock_code: null}))
      })));
      setSelectedProductItems([]);
    }
  };

  // Helper function to build add_to_cart tracking data
  const buildAddToCartTrackingData = (product: Product, productItem: ProductItem, selectedStockCode: string, quantity: number) => {
    const pharmacy = filteredPharmaciesForPickup[selectedStockCode] || allPharmaciesLocations[selectedStockCode];
    const pricePerUnit = productItem?.available_stocks?.find(s => s.stockCode === selectedStockCode)?.officialPrice || productItem?.official_price || 0;
    
    // Calculate price_per_gram
    const gramMatch = productItem?.custom_quantity_title?.match(/(\d+(?:\.\d+)?)\s*(?:g|gram|gramm)/i);
    const grams = gramMatch ? parseFloat(gramMatch[1]) : (product.quantity_type === 'gram' ? (productItem?.quantity || 0) : 0);
    const pricePerGram = (pricePerUnit > 0 && grams > 0) ? pricePerUnit / grams : null;
    
    // Calculate cart_total_value (after adding this item)
    const currentCartValue = calculateTotalMedicationPrice();
    const newItemPrice = pricePerUnit;
    let cartTotalValue = currentCartValue + newItemPrice;
    if (!treatAllAsRegular) {
      cartTotalValue += (calculateTotalTreatmentFee() || 0) + (cart?.consultation_fee || 0) + (calculateTotalServiceFee() || 0);
    }
    if (shippingMethod === constants.EXPRESS_SHIPPING) {
      cartTotalValue += (cart?.custom_shipping_fee || cart?.shipping_fee || 0);
    } else if (shippingMethod === constants.SHIPPING) {
      cartTotalValue += (cart?.shipping_fee || 0);
    }
    cartTotalValue = Math.max(0, cartTotalValue - (cart?.bonus_value || 0));
    
    MainTracker.track("custom", "add_to_cart", {
      user_id: customerState?.customerID || null,
      product_id: product.product_id,
      product_name: product.title,
      pharmacy_id: selectedStockCode || null,
      pharmacy_name: pharmacy?.name || null,
      price_per_gram: pricePerGram,
      quantity_grams: product.quantity_type === 'gram' ? quantity : null,
      cart_total_value: cartTotalValue
    });

  };

  const handleAddToOrderFromDetails = (quantity: number, selectedStockCode: string) => {
    if (selectedProductForDetails) {
      // Find the product item that matches the selected quantity
      const matchingProductItem = selectedProductForDetails.items.find(item => item.quantity === quantity);
      if (!matchingProductItem) return;
      
      matchingProductItem.selected_stock_code = selectedStockCode;

      // Track add_to_cart event before adding to cart
      buildAddToCartTrackingData(selectedProductForDetails, matchingProductItem, selectedStockCode, quantity);

      handleProductToggle({ selectedProduct: selectedProductForDetails, desiredProductItem: matchingProductItem, toggleAction: "ADD" });
      
      // Close the details popup
      handleProductDetailsClose();
    }
  };


  const totalPrice = calculateTotalMedicationPrice();

  // *** lazy load products ***
  const scrollToTop = useCallback(() => {
    const searchContainer = document.getElementById('search-and-products-container');

    if (searchContainer) {
      const rect = searchContainer.getBoundingClientRect();
      const scrollPosition = window.scrollY + rect.top;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Trigger server-side search when any filter/sort/search/shipping state changes.
  // Skip until the initial page-load products are in place (serverInitialized) so the
  // state updates from the first response don't cause a redundant ct-products-search call.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!ctSearch.serverInitialized) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    if (!categoryCatalog.id) return;

    const selectedProductIds = selectedProducts.map((p: any) => p.product_id);
    ctSearch.searchProducts(filterData, searchTerm, sortBy, selectedProductIds);
  }, [ctSearch.serverInitialized, filterData, searchTerm, sortBy, shippingMethod, PurchaseTierFilter, searchSelectedStockCode, externalPharmacyIds, searchAvailablePharmacyStockCodes, stockCodesInRange]);

const isFirstSearchEffect = useRef(true);
// Scroll only when user actually searches (not on first render)
useEffect(() => {
  // Skip first render
  if (isFirstSearchEffect.current) {
    isFirstSearchEffect.current = false;
    return;
  }

  // Scroll when the user typed a search term or changed a filter/sort. The list shrinks while
  // results reload, so without this the browser leaves the user at the bottom of the page.
  if (searchTerm.trim().length > 0 || filterChangedByUserRef.current) {
    scrollToTop();
  }
}, [searchTerm, filterData, sortBy, scrollToTop]);

  // Infinite scroll — load more products from server when user scrolls near bottom
  useEffect(() => {
    const handleScroll = () => {
      const leftPanels = document.getElementById('left-panels');
      if (!leftPanels) return;

      const rect = leftPanels.getBoundingClientRect();
      const scrollPosition = window.innerHeight + window.scrollY;
      const elementBottom = rect.bottom + window.scrollY;

      if (scrollPosition > elementBottom - 1000 && ctSearch.hasMore && !ctSearch.isLoadingMore) {
        ctSearch.loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ctSearch.hasMore, ctSearch.isLoadingMore, ctSearch.loadMore]);


  const freeDosageContext = {
    totalQuantity,
    desiredProductItem,
    setDesiredProductItem,
    setShowExceededMaxQuotaModal,
    reachedMaxQuota,
    disableUpQuantity,
    disableAddProduct,
    allowedCannabisGramsQuota,
    cannabisLimits,
    nextDesiredProductItem,
    language,
    maxCartProducts,
    products,
    selectedProducts,
    selectedProductItems,
    calculateTotalServiceFee,
    getServiceFeeTestInfo,
    handleProductToggle,
    handleProductItemToggle,
    calculateTotalTreatmentFee,
    allProductsTotalTreatmentFee,
    calculateFastApprovalFee,
    fastApprovalFeeFromServer,
    searchTerm,
    setSearchTerm,
    onSubmit,
    categoryLimitation,
    submitLoading,
    setSubmitLoading,
    filterData,
    toggleFilter,
    isFilterMenuOpen,
    setIsFilterMenuOpen: handleFilterMenuToggle,
    PurchaseTierFilter,
    setPurchaseTierFilter,
    appliedFiltersCount,
    filteredProducts,
    filteredProductsTotal: ctSearch.totalProducts,
    hideTags: true,
    hidePrices: isCannaleoFlow,
    sortBy, 
    setSortBy: (value: React.SetStateAction<SortByType>) => {
      filterChangedByUserRef.current = true;
      setSortBy(value);
    },
    searchEventCount,
    resetAllFilters: () => {
      filterChangedByUserRef.current = true;
      // 1. Reset all filters
      setFilterData(prev => 
        Object.keys(prev).reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: prev[curr].map(item => ({
              ...item,
              isSelected: false,
            })),
          }),
          {} as typeof filterData
        ));

      // 2. Reset sort by
      setSortBy(null);
      if (selectedProducts.length === 0) {
        setTreatAllAsRegular(false);
      }
    },
    productCardRefs,
    selectedProductForDetails,
    setSelectedProductForDetails,
    setViewOrder,
    setSelectedProductItems,
    calculateTotalMedicationPrice,
    calculateTotalShippingFee,
    setShowMaxLimitModal,
    scrollToMainTitle,
    isExpressShippingInRange,
    isExpressFlow,
    isExpressUberFlow,
    isExpressUrbifyFlow,
    isFastApprovalFlow,
    treatAllAsRegular,
    setTreatAllAsRegular,
    defaultProductItemIndex,
    selectedStockCode,
    cart,
    setCart,
    shippingMethod,
    setShippingMethod,
    selectedService,
    setSelectedService,
    handleProductDetailsOpen,
    setExpressShippingCompany,
    setProducts,
    parseProducts,
    customerZipcode,
    setCustomerZipcode,
    setSelectedStockCode,
    stockCodesInRange,
    setStockCodesInRange,
    expressStockOptions,
    setExpressStockOptions,
    selectedExpressTier,
    setSelectedExpressTier,
    handleExpressTierSelect,
    handleSameDayCutoffExpired,
    selectedApprovalService,
    setSelectedApprovalService,
    isSelfPickupFlow,
    isExpressPrescriptionReview,
    setIsExpressPrescriptionReview,
    allPharmaciesLocations,
    setAllPharmaciesLocations,
    setNearbyPharmacies,
    filteredPharmaciesForPickup,
    showSparkleLabel: abTest.showSparkleLabel,
    onExpressLabelClick,
    onSelfPickupLabelClick,
    trackSelfPickupView,
    isExpressNotEligible,
    resetAllCarts,
    resetMethodSelection,
    resetExpressTierCarts,
    isReturningCustomer,
    isCannabisReturningCustomer,
    onFavoriteChange,
    isUrbifyFlow,
    customerAddresses,
    oosPositionsWithOffers
  }

  return (
    <ChooseTreatmentContext.Provider value={freeDosageContext}>
    <CannabisPharmacyPickerProvider>
      {loading && <LoadingModal />}

      <StickyNotification
        showNotification={stickyNotification.show}
        title={stickyNotification.title}
        onClick={() => setStickyNotification({ show: false, title: "", content: "", level: "", showInfoIcon: false, autoFade: false })}
        content={stickyNotification.content}
        level={stickyNotification.level}
        refresh={stickyNotification.refresh}
        autoFade={stickyNotification.autoFade}
      />

      {alternativeOfferState.showModal && (
        <AlternativeOfferModalChooseTreatment
          alternatives={alternativeOfferState.offers}
          onAccept={handleAlternativeAccept}
          onCancel={handleAlternativeCancel}
          onWait={handleAlternativeWait}
          closeModal={handleCloseAlternativeModal}
          language={language}
        />
      )}

      {error && (
        <>
          <HeaderController showSteps={false} stage={"Questionnaire"} headerVersion={headerVersion} />
          <ServerError status={error} />
        </>
      )}

      {!loading && !error &&

        <>

      {/* Max Limit Modal */}
        <PortaledModal
          shouldBackgroundClickHideModal={true}
          shouldShowModal={showMaxLimitModal}
          backgroundColor='transparent'
          hideModal={() => setShowMaxLimitModal(false)}
          showModal={() => setShowMaxLimitModal(true)}
        >
          <div id="max-limit-modal-disclaimer" className="relative">
            <Disclaimer
              text={(translate(language, 'choose_treatment', 'cannabis_limits/max_products_reached') as string).replace('<maxProducts>', String(maxCartProducts))}
              containerClassName="w-[90%] sm:w-[464px] min-h-[120px] sm:min-h-[161px] bg-white border-2 border-solid border-[#269C6E] rounded-lg p-3 sm:p-4 pt-4 sm:pt-6 flex items-start gap-2 sm:gap-3"
              textClassName="text-[#384248] font-normal text-sm sm:text-base mt-1 sm:mt-2"
              iconClassName="text-[#00B388] w-4 h-4 sm:w-5 sm:h-5 mt-1"
            >
              <div className="text-[#0A9281] justify-center underline mt-4 md:mt-1 text-sm font-roboto font-medium cursor-pointer" onClick={() =>{
                // Track view_order_click event - group products by pharmacy
                const productByCatalogId = new Map(selectedProducts.map(p => [p.catalog_id, p]));
                const pharmacyGroups = new Map<string, any[]>();
                
                selectedProductItems.forEach(productItem => {
                  const product = productByCatalogId.get(productItem.catalog_id);
                  const pharmacyId = productItem.selected_stock_code || 'no_pharmacy';
                  
                  if (!pharmacyGroups.has(pharmacyId)) {
                    pharmacyGroups.set(pharmacyId, []);
                  }
                  
                  pharmacyGroups.get(pharmacyId)!.push({
                    product_id: product?.product_id || null,
                    name: product?.title || null,
                    qty: productItem.quantity,
                    price: productItem.official_price,
                    pharmacy_id: pharmacyId === 'no_pharmacy' ? null : pharmacyId
                  });
                });
                
                const cartProducts = Array.from(pharmacyGroups.values()).flat();
                
                MainTracker.track("custom", "view_order_click", {
                  user_id: customerState?.customerID || null,
                  cart_products: cartProducts,
                  cart_total_value: calculateTotalMedicationPrice()
                });
                
                setViewOrder(true);
                setShowMaxLimitModal(false)
              }}>{translate(language, 'choose_treatment', 'cannabis_limits/view_order')}</div>
            </Disclaimer>

            <div
              onClick={() => setShowMaxLimitModal(false)}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 cursor-pointer"
            >
              <img src={CloseIcon} width="16" height="16" className="sm:w-5 sm:h-5" alt="Close Icon" />
            </div>
          </div>


        </PortaledModal>

        {/* Exceeded Max Quota Modal */}

        <PortaledModal
          shouldBackgroundClickHideModal={true}
          shouldShowModal={showExceededMaxQuotaModal}
          backgroundColor='transparent'
          hideModal={() => setShowExceededMaxQuotaModal(false)}
          showModal={() => setShowExceededMaxQuotaModal(true)}
        >
          <div id="exceeded-max-quota-modal-disclaimer" className="relative">
            <Disclaimer
              text = {rateLimited ? cannabisLimitMessage : translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_quota').replace('<maxQuota>', String(cannabisLimits?.monthlyLimit || DEFAULT_MONTHLY_CANNABIS_PURCHASED_QUANTITY_LIMIT)).replace('<quota>', String(allowedCannabisGramsQuota))}
              containerClassName="w-[90%] sm:w-[464px] min-h-[120px] sm:min-h-[161px] bg-white border-2 border-solid border-[#269C6E] rounded-lg p-3 sm:p-4 pt-4 sm:pt-6 flex items-start gap-2 sm:gap-3"
              textClassName="text-[#384248] font-normal text-sm sm:text-base mt-1 sm:mt-2"
              iconClassName="text-[#00B388] w-4 h-4 sm:w-5 sm:h-5 mt-1"
            ></Disclaimer>
          </div>
        </PortaledModal>

          {/* DoktorABC Nav  */}
          {category && <HeaderController language={language} stage="Treatment" headerVersion={headerVersion} />}

          {/* Main container  */}
          <main id='main-container' className='flex min-h-screen p-4 md:pt-10 bg-[var(--primary-gray)] flex-col items-center'>

            {/* Content Container  */}
            <div id='content-container' className='w-full md:max-w-[550px] flex flex-col items-center justify-center'>

              {/* Header  */}
              <header className='w-full flex flex-col gap-y-2'>
                <h1 id='main-title' className='flex text-2xl lg:text-4xl justify-center text-[#0D2C54]'>{translate(language, 'choose_treatment', 'default/choose_preferred_treatment')}</h1>
              </header>

              {/* Sticky Price Text  */}
              <div id="sticky-price-text-ct" className='w-full sticky top-0 pin-below-app-bar z-20 bg-[var(--primary-gray)] pt-2 pb-2 mb-0'>
                <p className='flex text-[16px] justify-center text-[#0D2C54] font-roboto font-normal'>{translate(language, 'choose_treatment', 'default/price_incl_vat')}</p>
              </div>

               {/* Shipping Method and Service Selection Dropdowns */}
               {!isSwissCannabis && (
                 <ShippingAndServiceDropdowns />
               )}

              {/* Disclaimer  */} 
              <div id='header-disclaimer' style={{ fontFamily: "Roboto, sans-serif" }} className='w-full flex flex-col gap-2 mb-4 text-center text-[#161718] text-[16px] font-normal break-words'>
                {selectedExternalPharmacy ? ` (${selectedExternalPharmacy.cannabis_pharmacy_name})` : ""}
              </div>

              {/* View Order  */}
              <ViewOrder viewOrder={viewOrder} setStickyNotification={setStickyNotification} />    

              {/* Self-Pickup Map  */}
              {shippingMethod === constants.PICKUP && (
                <>
              <span className="w-full m-2 text-left text-[14px] text-[#363636] font-roboto font-medium">
                    {translate(language, 'choose_treatment', 'default/search_by_pharmacy').replace('{count}', Object.keys(filteredPharmaciesForPickup).length.toString())}
              </span>
              <div className='w-full flex flex-col bg-white py-2 px-2 rounded-lg gap-2'>
                <div id="pharmacy-search-section" className="flex flex-col w-full gap-2">

                  <PharmacySearch 
                    stockLocations={filteredPharmaciesForPickup}
                    onPharmacySelect={(stockCode) => {
                      handlePharmacySelection(stockCode);
                      clearPrescriptionOnlySelections();
                    }}
                    onPharmacyHighlight={setHighlightedStockCode}
                    highlightedStockCode={highlightedStockCode}
                    productsCountPerPharmacy={calculateProductsCountPerPharmacy}
                    externalSearchQuery={pharmacySearchQuery}
                  />
                </div>
                <PharmacyCard
                    pharmacyId={selectedStockCode}
                    pharmacy={selectedStockCode ? allPharmaciesLocations[selectedStockCode] : null}
                    onCancel={() => {
                      setSelectedStockCode(null);
                      setPharmacySearchQuery("");
                      clearPrescriptionOnlySelections();
                    }}
                  />
                  {/* Keep PharmacyMap mounted but hide when pharmacy is selected */}
                  <div className="px-2.5 pb-2.5" style={{ display: selectedStockCode ? 'none' : 'block' }}>
                    <PharmacyMap 
                      allPharmaciesLocations={filteredPharmaciesForPickup}
                      selectedStockCode={selectedStockCode} 
                      onPharmacySelect={(stockCode) => {
                        if (stockCode) {
                          handlePharmacySelection(stockCode);
                          clearPrescriptionOnlySelections();
                        }
                      }}
                      highlightedStockCode={highlightedStockCode}
                      onPharmacyHighlight={setHighlightedStockCode}
                      productsCountPerPharmacy={calculateProductsCountPerPharmacy}
                      onLocationUsed={(userLocation, nearbyPharmaciesFromMap) => {
                        // Add products count to each pharmacy and update state
                        const pharmaciesWithCounts = nearbyPharmaciesFromMap.map(pharmacy => ({
                          ...pharmacy,
                          productsCount: calculateProductsCountPerPharmacy[pharmacy.stockCode] || 0
                        }));
                        setNearbyPharmacies(pharmaciesWithCounts);
                      }}
                    />
                  </div>
                  {/* Pharmacy List - shown when location is used */}
                  {!selectedStockCode && nearbyPharmacies.length > 0 && (
                    <div className="mt-4">
                      <PharmacyList
                        pharmacies={nearbyPharmacies}
                        selectedStockCode={selectedStockCode}
                        onPharmacySelect={handlePharmacySelection}
                        onPharmacyHighlight={setHighlightedStockCode}
                        onShowProducts={(stockCode) => {
                          handlePharmacySelection(stockCode);
                          // Scroll to products section if needed
                        }}
                      />
                    </div>
                  )}
                </div>
                </>
              )}

              {submitLoading ? (
                    <div style={{width: "92px", height: "92px", marginTop: "20px", border: "9px #cdcdcd solid", borderTop: "9px #269C6E solid", borderRadius: "50%", animation: "spCircRot 0.8s infinite linear", WebkitAnimation: "spCircRot 0.8s infinite linear"}}></div>
              ) : isExpressNotEligible ? null :
              <div id="search-and-products-container" className={`${viewOrder ? 'h-0 opacity-30' : 'opacity-100'} transition-opacity duration-500 w-full max-w-full min-w-0`}>
                {/* Search & Desktop disclaimer  */}
                {!isExpressNotEligible && (
                  <div id='search-and-disclaimer' style={{ boxShadow: "0 25px 25px #f7f7f7", backgroundColor: "#f7f7f7"}} className='sticky top-[40px] pin-below-app-bar pin-offset-40 w-full flex flex-col gap-1 mb-5 z-10'>
                    {shippingMethod === constants.PICKUP && <span className='text-[14px] text-[#363636] font-roboto font-medium mt-4'>{translate(language, 'choose_treatment', 'default/search_by_product')}</span> }
                    <div id='header-search' className='flex flex-row w-full justify-between items-center gap-4'>
                      <ProductSearch className="flex-1 min-w-0" />
                      <ProductFiltersControl />
                    </div>
                    {/* Inline Product Type Filter */}
                    {filterData['type'] && (
                      <CannabisProductTypeFilter
                        layoutVersion="chooseTreatmentHeader"
                        filterOptions={filterData['type']}
                        handleChooseFilter={(item: { value: string, isSelected: boolean }, isMultiSelect?: boolean) => () => {
                          toggleFilter('type', item.value, isMultiSelect);
                        }}
                      />
                    )}
                  </div>
                )}


                {/* Mobile Disclaimer And Panels Container  */}
                <div id='mobile-disclaimer-and-panels-container' className='w-full'>

                  {/* Panels Container  */}
                  <div id='panels-container' ref={panelsContainerRef} className='grid grid-col-1 md:grid-cols-1 gap-8 w-full'>

                    {/* left panel */}
                    <div id='left-panels' className={`flex flex-col flex-1`}>
                      
                      {/* Selected Products Section */}
                      {shouldShowSelectedProductsSection && (
                        <>
                          <span id='selected-products-panel-title' style={{ fontFamily: "Roboto, sans-serif" }} className='font-medium mb-2 text-[13px] text-[#98A0AF] mt-2'>Ihr Warenkorb</span>
                          <ProductSelectionPanel
                            flow={cart?.flow}
                            products={selectedProducts} 
                            productCardClassName="mb-1"
                          />
                        </>
                      )}

                      {/* Single Product Panel with Title */}
                      {ctSearch.isSearching ? (
                        <div className="flex justify-center py-16">
                          <ImSpinner2 className="w-8 h-8 text-[#00B388] animate-spin" />
                        </div>
                      ) : (
                        <>
                          <span id='all-products-panel-title' style={{ fontFamily: "Roboto, sans-serif" }} className='font-medium my-2 text-[13px] text-[#98A0AF]'>
                            {translate(language, 'choose_treatment', 'default/all_products') || 'Alle Produkte'} <span id='filtered-products-amount' className='notranslate'> ({ctSearch.totalProducts || filteredProducts?.length || 0}) </span>
                          </span>
                          <ProductSelectionPanel
                            flow={cart?.flow}
                            products={filteredProducts || []}
                          />
                          {ctSearch.isLoadingMore && (
                            <div className="flex justify-center py-6">
                              <ImSpinner2 className="w-8 h-8 text-[#00B388] animate-spin" />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              }
            </div>
          </main>

        

        {/* Product Details Card */}
        {selectedProductForDetails && (
          <ProductDetailsCard
            isVisible={showProductDetails}
            onClose={handleProductDetailsClose}
            product={selectedProductForDetails}
            onAddToOrder={handleAddToOrderFromDetails}
            stocksLocations={filteredPharmaciesForPickup}
          />
        )}

        {/* DoktorABC Footer  */}
        {category && !viewOrder && <Footer showDiscountDisclaimer={true} page="ChooseTreatment" language={language} />} {/*shows footer in normal flow but not in modal*/}

        {/* Self-Pickup Walkthrough */}
        {showWalkthrough && walkthroughStep > 0 && (
          <SelfPickupWalkthrough
            language={language}
            isVisible={showWalkthrough}
            onClose={handleWalkthroughClose}
            onComplete={handleWalkthroughComplete}
            step={walkthroughStep}
            onStepChange={setWalkthroughStep}
          />
        )}

        {!viewOrder &&
          <>
            {/* Filters Menu - hidden when express selected but not eligible */}
            {!isExpressNotEligible && (
              <FilterMenu 
                filterData={filterData} 
                hidePremiumOrRegularFilter={selectedProductItems.length > 0} 
              />
            )}

            {/* Sticky Submit  */}
            <StickySubmitButton
              showDisclaimer={false}
              disclaimerText={(translate(language, 'choose_treatment', 'cannabis_limits/max_products_reached') as string).replace('<maxProducts>', String(maxCartProducts))}
              buttonDisabled={submitLoading || selectedProducts.length === 0 || alternativeOfferState.isLoading}
              onSubmit={() => {
                // Track view_order_click event - group products by pharmacy
                const productByCatalogId = new Map(selectedProducts.map(p => [p.catalog_id, p]));
                const pharmacyGroups = new Map<string, any[]>();
                
                selectedProductItems.forEach(productItem => {
                  const product = productByCatalogId.get(productItem.catalog_id);
                  const pharmacyId = productItem.selected_stock_code || 'no_pharmacy';
                  
                  if (!pharmacyGroups.has(pharmacyId)) {
                    pharmacyGroups.set(pharmacyId, []);
                  }
                  
                  pharmacyGroups.get(pharmacyId)!.push({
                    product_id: product?.product_id || null,
                    name: product?.title || null,
                    qty: productItem.quantity,
                    price: productItem.official_price,
                    pharmacy_id: pharmacyId === 'no_pharmacy' ? null : pharmacyId
                  });
                });
                
                const cartProducts = Array.from(pharmacyGroups.values()).flat();
                
                MainTracker.track("custom", "view_order_click", {
                  user_id: customerState?.customerID || null,
                  cart_products: cartProducts,
                  cart_total_value: calculateTotalMedicationPrice()
                });
                
                setViewOrder(true)
              }}
              selectedProductsAmount= {selectedProductItems.length}
              totalPrice={totalPrice}
              locale={cart?.locale_id || 'de-DE'}
            />
          </>
        }
        </>
      }

      {/* Unknown Stock Confirmation Modal */}
      {pendingPharmacySelection && (
        <UnknownStockConfirmationModal
          isOpen={showUnknownStockModal}
          onClose={cancelUnknownStockSelection}
          onConfirm={confirmUnknownStockSelection}
          pharmacyName={allPharmaciesLocations[pendingPharmacySelection]?.name || ''}
          pharmacyAddress={allPharmaciesLocations[pendingPharmacySelection]?.address || ''}
        />
      )}
    </CannabisPharmacyPickerProvider>
    </ChooseTreatmentContext.Provider >
  )
}

export default FreeDosageLayoutV2